import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import styled, { createGlobalStyle, keyframes } from 'styled-components';

const flicker = keyframes`
  0%, 100% { opacity: 1; filter: brightness(1); }
  50% { opacity: 0.92; filter: brightness(1.15); }
`;

const scanline = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100vh); }
`;

const glitch = keyframes`
  0% { transform: translate(0); }
  20% { transform: translate(-2px, 1px); }
  40% { transform: translate(2px, -1px); }
  60% { transform: translate(-1px, 2px); }
  80% { transform: translate(1px, -2px); }
  100% { transform: translate(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.03); opacity: 0.9; }
`;

const logFadeIn = keyframes`
  from { opacity: 0; transform: translateX(-6px); }
  to { opacity: 1; transform: translateX(0); }
`;
const graphNodeIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;

const spinGlobe = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;
const spinOrbit = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
`;
const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 15px rgba(0, 255, 136, 0.25), inset 0 0 10px rgba(0, 255, 136, 0.05); }
  50% { box-shadow: 0 0 25px rgba(0, 255, 136, 0.4), inset 0 0 15px rgba(0, 255, 136, 0.1); }
`;

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&display=swap');
  
  * {
    box-sizing: border-box;
  }
  
  body {
    margin: 0;
    padding: 0;
    background: #050508;
    background-image: 
      radial-gradient(ellipse at 20% 30%, rgba(0, 40, 30, 0.4) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 70%, rgba(0, 30, 40, 0.3) 0%, transparent 50%),
      linear-gradient(180deg, #050508 0%, #0a0c10 100%);
    font-family: 'JetBrains Mono', 'Fira Code', 'Source Code Pro', monospace;
    color: var(--da-accent, #00ff88);
    overflow-x: hidden;
    min-height: 100vh;
    scrollbar-width: thin;
    scrollbar-color: #00ff88 #111;
  }
  
  body::after {
    content: "";
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 3px,
      rgba(0, 255, 136, 0.015) 3px,
      rgba(0, 255, 136, 0.015) 6px
    );
    pointer-events: none;
    z-index: 1000;
  }
  
  body::before {
    content: "";
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0, 255, 136, 0.02) 50%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1001;
    animation: ${scanline} 12s linear infinite;
    opacity: 0.6;
  }
  
  ::-webkit-scrollbar {
    width: 6px;
    background: #0a0a0c;
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #00ff88, #00cc6a);
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: #00ffaa;
  }
  @keyframes spinOrbit {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(-360deg); }
  }
  @keyframes logFadeIn {
    from { opacity: 0; transform: translateX(-6px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes graphNodeIn {
    from { opacity: 0; transform: scale(0.92); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes spinGlobe {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LayoutRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
  @media (max-width: 1100px) {
    flex-direction: column;
  }
`;
const ChatCol = styled.div`
  flex: 1;
  min-width: 0;
`;
const GlobeCol = styled.div`
  width: 860px;
  flex-shrink: 0;
  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const MainTabBar = styled.div`
  display: flex;
  flex-direction: column;
  width: 220px;
  min-width: 200px;
  gap: 6px;
  padding: 10px;
  margin: 0;
  flex-shrink: 0;
  position: sticky;
  top: 8px;
  align-self: flex-start;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  background: rgba(4, 14, 11, 0.72);
  backdrop-filter: blur(18px);
  border-radius: 16px;
  border: 1px solid rgba(0, 255, 136, 0.28);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(0, 255, 136, 0.08);
`;

const MainTabBtn = styled.button`
  width: 100%;
  text-align: left;
  background: ${p => p.active
    ? 'linear-gradient(135deg, rgba(0,255,136,0.95), rgba(0,204,106,0.85))'
    : 'rgba(0, 18, 14, 0.55)'};
  color: ${p => p.active ? '#001a10' : '#8fd4b0'};
  border: 1px solid ${p => p.active ? '#00ffaa' : 'rgba(0,255,136,0.22)'};
  padding: 11px 14px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  white-space: normal;
  line-height: 1.35;
  transform: ${p => (p.active ? 'translateX(5px)' : 'none')};
  box-shadow: ${p => (p.active ? '0 0 22px rgba(0, 255, 136, 0.35)' : 'none')};
  &:hover {
    color: ${p => (p.active ? '#001a10' : '#00ff88')};
    border-color: #00ff88;
    box-shadow: 0 0 16px rgba(0, 255, 136, 0.28);
  }
`;

const GuidePanel = styled.div`
  border: 1px solid rgba(0, 255, 136, 0.25);
  border-radius: 12px;
  background: rgba(8, 10, 14, 0.9);
  padding: 28px 32px;
  max-width: 900px;
  margin: 0 auto;
  max-height: 75vh;
  overflow-y: auto;
  font-size: 15px;
  line-height: 1.65;
  color: #b8ffd4;
  h2 { color: #00ff88; font-size: 20px; margin: 24px 0 10px; letter-spacing: 1px; }
  h3 { color: #00ccaa; font-size: 16px; margin: 16px 0 8px; }
  p, li { margin: 6px 0; }
  ul { padding-left: 22px; }
  code { background: rgba(0,255,136,0.1); padding: 2px 6px; border-radius: 4px; color: #00ffaa; }
  .step { border-left: 3px solid #00ff88; padding: 8px 14px; margin: 10px 0; background: rgba(0,30,20,0.4); }
`;

const Container = styled.div`
  position: relative;
  z-index: 10;
  max-width: 2000px;
  margin: 30px auto;
  padding: 16px 20px;
`;


const DashboardBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding: 14px 18px;
  border-radius: 14px;
  border: 1px solid var(--da-accent-dim, rgba(0, 255, 136, 0.28));
  background: linear-gradient(135deg, rgba(0, 14, 11, 0.88), rgba(0, 8, 6, 0.92));
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35), inset 0 1px 0 var(--da-accent-soft, rgba(0, 255, 136, 0.1));
`;

const StatChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.4px;
  color: var(--da-accent, #00ff88);
  border: 1px solid var(--da-accent-dim, rgba(0, 255, 136, 0.25));
  background: var(--da-accent-soft, rgba(0, 255, 136, 0.08));
  b { font-weight: 800; }
`;

const EmptyHero = styled.div`
  text-align: center;
  padding: 36px 24px 28px;
  margin-bottom: 16px;
  border-radius: 16px;
  border: 1px dashed var(--da-accent-dim, rgba(0, 255, 136, 0.35));
  background: radial-gradient(ellipse at 50% 0%, var(--da-accent-soft, rgba(0, 255, 136, 0.1)), transparent 60%);
`;

const LogPanel = styled.div`
  max-height: 280px;
  overflow-y: auto;
  margin-bottom: 12px;
  padding: 8px 4px;
  border-radius: 12px;
  border: 1px solid rgba(0, 255, 136, 0.12);
  background: rgba(0, 6, 5, 0.45);
`;

const StepperTrack = styled.div`
  display: flex;
  gap: 3px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const StepDot = styled.div`
  flex: 1 1 8px;
  min-width: 6px;
  max-width: 24px;
  height: 8px;
  border-radius: 4px;
  background: ${p => p.$done ? 'var(--da-accent, #00ff88)' : p.$run ? '#ffcc44' : p.$err ? '#ff6688' : 'rgba(80,90,85,0.5)'};
  box-shadow: ${p => p.$run ? '0 0 8px #ffcc44' : p.$done ? '0 0 6px var(--da-accent-glow, rgba(0,255,136,0.4))' : 'none'};
  transition: background 0.25s ease, box-shadow 0.25s ease;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 28px;
  animation: ${pulse} 3s ease-in-out infinite;
`;

const LogoText = styled.h1`
  font-size: 42px;
  font-weight: 800;
  letter-spacing: 8px;
  color: #00ff88;
  text-transform: uppercase;
  margin: 0;
  text-shadow: 
    0 0 10px rgba(0, 255, 136, 0.8),
    0 0 30px rgba(0, 255, 136, 0.4),
    0 0 60px rgba(0, 255, 136, 0.2);
  animation: ${flicker} 4s infinite;
  background: linear-gradient(90deg, #00ff88, #00ccff, #00ff88);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SubText = styled.p`
  font-size: 13px;
  color: #00ccaa;
  letter-spacing: 6px;
  text-transform: uppercase;
  opacity: 0.75;
  margin-top: 6px;
  font-weight: 500;
`;

const MainPanel = styled.div`
  border: 1px solid rgba(0, 255, 136, 0.28);
  border-radius: 16px;
  background: linear-gradient(165deg, rgba(8, 14, 12, 0.88), rgba(4, 8, 7, 0.92));
  backdrop-filter: blur(14px);
  box-shadow:
    0 0 48px rgba(0, 255, 136, 0.06),
    0 12px 40px rgba(0, 0, 0, 0.55),
    inset 0 1px 0 rgba(0, 255, 136, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PanelTitle = styled.div`
  background: linear-gradient(90deg, rgba(0, 20, 15, 0.9), rgba(0, 15, 20, 0.9));
  padding: 10px 18px;
  color: #00ccaa;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid rgba(0, 255, 136, 0.2);
  text-transform: uppercase;
  letter-spacing: 3px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelContent = styled.div`
  height: 560px;
  overflow-y: auto;
  padding: 14px 16px;
  background: rgba(5, 6, 8, 0.4);
`;

const SubTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 255, 136, 0.1);
`;

const SubTab = styled.button`
  background: ${p => p.tone === 'ok' ? 'rgba(0,255,136,0.16)' : p.tone === 'err' ? 'rgba(255,200,0,0.14)' : 'rgba(0,20,15,0.45)'};
  color: ${p => p.tone === 'ok' ? '#00ff88' : p.tone === 'err' ? '#e6c84a' : '#6e8f80'};
  border: 1px solid ${p => p.tone === 'ok' ? '#00ff88' : p.tone === 'err' ? '#c9a227' : 'rgba(110,143,128,0.35)'};
  opacity: ${p => p.active ? 1 : 0.85};
  box-shadow: ${p => p.active ? '0 0 10px rgba(0,255,136,0.25)' : 'none'};
  padding: 6px 10px;
  font-family: inherit;
  font-size: 12px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
`;

const Prompt = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 18px;
  background: rgba(6, 8, 12, 0.95);
  border-top: 1px solid rgba(0, 255, 136, 0.15);
`;

const Input = styled.input`
  background: transparent;
  border: none;
  color: #33ff99;
  font-family: inherit;
  font-size: 18px;
  outline: none;
  flex: 1;
  caret-color: #00ff88;
  
  &::placeholder {
    color: rgba(0, 255, 136, 0.35);
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, rgba(0, 40, 30, 0.9), rgba(0, 30, 40, 0.9));
  border: 1px solid #00ff88;
  color: #00ff88;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  padding: 12px 22px;
  margin-left: 12px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 0 12px rgba(0, 255, 136, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, #00ff88, #00cc6a);
    color: #001a10;
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.5);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  &:disabled {
    opacity: 0.45;
    cursor: wait;
    filter: grayscale(0.3);
    box-shadow: none;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, rgba(0, 30, 25, 0.9), rgba(0, 25, 35, 0.9));
  border: 1px solid #00ff88;
  color: #00ff88;
  padding: 14px 24px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  border-radius: 6px;
  transition: all 0.25s ease;
  margin-top: 8px;
  box-shadow: 0 0 10px rgba(0, 255, 136, 0.15);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #00ff88, #00cc6a);
    color: #001a10;
    box-shadow: 0 0 18px rgba(0, 255, 136, 0.4);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AuthForm = styled.form`
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 380px;
  margin: 0 auto;
  background: rgba(8, 10, 14, 0.9);
  border: 1px solid rgba(0, 255, 136, 0.2);
  border-radius: 12px;
  box-shadow: 0 0 40px rgba(0, 255, 136, 0.1);
`;

const AuthInput = styled(Input)`
  border: 1px solid rgba(0, 255, 136, 0.3);
  padding: 12px 14px;
  margin: 8px 0;
  background: rgba(0, 10, 8, 0.6);
  border-radius: 6px;
  width: 100%;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    border-color: #00ff88;
    box-shadow: 0 0 12px rgba(0, 255, 136, 0.25);
  }
`;

const AuthTitle = styled.h2`
  text-transform: uppercase;
  letter-spacing: 4px;
  margin-bottom: 24px;
  font-size: 18px;
  color: #00ff88;
  text-shadow: 0 0 15px rgba(0, 255, 136, 0.5);
  animation: ${glitch} 3s infinite;
`;

const ReportBlock = styled.div`
  border: 1px solid rgba(0, 255, 136, 0.2);
  border-left: 3px solid #00ff88;
  padding: 10px 14px;
  margin: 8px 0;
  background: rgba(0, 15, 12, 0.5);
  border-radius: 0 6px 6px 0;
  font-size: 16px;
  line-height: 1.55;
`;

const ListContainer = styled.div`
  max-height: 420px;
  overflow-y: auto;
  background: rgba(0, 8, 6, 0.6);
  border: 1px solid rgba(0, 255, 136, 0.15);
  border-radius: 6px;
  padding: 10px 12px;
  margin: 8px 0;
  font-size: 13px;
`;


const VizSection = styled.div`
  height: 100%;
  min-height: 920px;
  padding: 22px 18px;
  border: 1px solid rgba(0, 255, 136, 0.32);
  border-radius: 20px;
  background:
    radial-gradient(ellipse at 50% 0%, rgba(0, 255, 136, 0.08), transparent 55%),
    linear-gradient(180deg, rgba(0, 14, 11, 0.96), rgba(0, 6, 5, 0.98));
  box-shadow:
    0 0 60px rgba(0, 255, 136, 0.1),
    inset 0 1px 0 rgba(0, 255, 136, 0.15),
    inset 0 -20px 40px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
`;

const GlobeWrap = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 720px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const GlobeCore = styled.div`
  position: absolute;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #00ff88, #003322 70%, #001a10);
  border: 2px solid #00ff88;
  box-shadow: 0 0 40px rgba(0, 255, 136, 0.5), inset 0 0 20px rgba(0, 255, 136, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #001a10;
  z-index: 5;
  padding: 8px;
  word-break: break-all;
  line-height: 1.15;
  &::before {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    border: 1px dashed rgba(0, 255, 136, 0.4);
    animation: ${spinGlobe} 20s linear infinite;
  }
  &::after {
    content: '';
    position: absolute;
    inset: -14px;
    border-radius: 50%;
    border: 1px solid rgba(0, 255, 136, 0.15);
    border-top-color: #00ff88;
    animation: ${spinGlobe} 12s linear infinite;
  }
`;
const CveNode = styled.button`
  position: absolute;
  background: rgba(0, 20, 15, 0.95);
  border: 1px solid ${props => props.active ? '#ffcc00' : '#00ff88'};
  color: ${props => props.active ? '#ffcc00' : '#00ff88'};
  border-radius: 8px;
  padding: 6px 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  z-index: 4;
  max-width: 160px;
  padding: 8px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 0 12px rgba(0, 255, 136, 0.25);
  transition: all 0.2s ease;
  transform: translate(-50%, -50%);
  &:hover {
    background: #00ff88;
    color: #001a10;
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.6);
    z-index: 6;
  }
`;
const CveModal = styled.div`
  margin-top: 16px;
  border: 1px solid #ffcc00;
  border-left: 3px solid #ffcc00;
  background: rgba(20, 15, 0, 0.7);
  border-radius: 0 8px 8px 0;
  padding: 14px 16px;
  font-size: 13px;
  line-height: 1.5;
`;

const SmallButton = styled.button`
  background: rgba(0, 30, 25, 0.7);
  border: 1px solid rgba(0, 255, 136, 0.4);
  color: #00ff88;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 8px;
  margin-left: 6px;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    background: #00ff88;
    color: #001a10;
    box-shadow: 0 0 8px rgba(0, 255, 136, 0.5);
  }
`;

const parseWhois = (raw) => {
  if (!raw) return {};
  try {
    const obj = JSON.parse(raw);
    return {
      'Домен': obj.domain_name,
      'Регистратор': obj.registrar,
      'Организация': obj.org,
      'Дата создания': Array.isArray(obj.creation_date) ? obj.creation_date.join(', ') : obj.creation_date,
      'Дата окончания': Array.isArray(obj.expiration_date) ? obj.expiration_date.join(', ') : obj.expiration_date,
      'Дата обновления': Array.isArray(obj.updated_date) ? obj.updated_date.join(', ') : obj.updated_date,
      'Страна': obj.country,
      'NS серверы': Array.isArray(obj.name_servers) ? obj.name_servers.join(', ') : obj.name_servers,
      'Email': Array.isArray(obj.emails) ? obj.emails.join(', ') : obj.emails,
      'Статус': Array.isArray(obj.status) ? obj.status.join(', ') : obj.status,
      'DNSSEC': obj.dnssec,
      'Имя регистранта': obj.registrant_name || obj.name,
      'Адрес': obj.registrant_address || obj.address,
      'Город': obj.registrant_city || obj.city,
      'Штат': obj.registrant_state || obj.state,
      'Индекс': obj.registrant_postal_code || obj.registrant_postal_code,
      'Телефон': obj.registrant_phone || obj.phone,
    };
  } catch (e) { return { 'Сырой whois': raw.substring(0, 500) }; }
};


function CveGlobe3D({ domain, cves, cvssColor, sourceColor, onSelect, onHover, selectedId }) {
  const mountRef = useRef(null);
  const stateRef = useRef({ cves: [], onSelect: null });
  stateRef.current = { cves: cves || [], onSelect, onHover };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const w = mount.clientWidth || 600;
    const h = mount.clientHeight || 520;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050a08);
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.z = 5.4;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.innerHTML = '';
    mount.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0x00ff88, 0.35);
    scene.add(ambient);
    const light = new THREE.PointLight(0x66ffcc, 1.2, 20);
    light.position.set(4, 3, 5);
    scene.add(light);

    // globe
    const globeGeo = new THREE.SphereGeometry(1.05, 48, 48);
    const globeMat = new THREE.MeshPhongMaterial({
      color: 0x0a2a1a,
      emissive: 0x003322,
      shininess: 40,
      transparent: true,
      opacity: 0.92,
      wireframe: false,
    });
    const globe = new THREE.Mesh(globeGeo, globeMat);
    scene.add(globe);

    const makeCveLabel = (text, color) => {
      const canvas = document.createElement("canvas");
      canvas.width = 256; canvas.height = 64;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, 512, 128);
      ctx.font = "bold 20px monospace";
      ctx.fillStyle = color || "#00ff88";
      ctx.strokeStyle = "rgba(0,0,0,0.9)";
      ctx.lineWidth = 3;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const label = String(text || "").slice(0, 18);
      ctx.strokeText(label, 128, 32);
      ctx.fillText(label, 128, 32);
      const tex = new THREE.CanvasTexture(canvas);
      tex.needsUpdate = true;
      const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
      const spr = new THREE.Sprite(mat);
      spr.scale.set(0.95, 0.24, 1);
      return spr;
    };

    const wire = new THREE.Mesh(
      new THREE.SphereGeometry(1.06, 24, 24),
      new THREE.MeshBasicMaterial({ color: 0x00ff88, wireframe: true, transparent: true, opacity: 0.15 })
    );
    scene.add(wire);

    // latitude/longitude rings
    const ringMat = new THREE.LineBasicMaterial({ color: 0x00ff88, transparent: true, opacity: 0.2 });
    for (let i = 0; i < 6; i++) {
      const ring = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(
          Array.from({ length: 64 }, (_, j) => {
            const a = (j / 64) * Math.PI * 2;
            return new THREE.Vector3(Math.cos(a) * 1.08, (i - 2.5) * 0.35, Math.sin(a) * 1.08);
          })
        ),
        ringMat
      );
      scene.add(ring);
    }

    // domain label sprite-like: small core
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(0.12, 16, 16),
      new THREE.MeshBasicMaterial({ color: 0x00ff88 })
    );
    scene.add(core);
    const pointsGroup = new THREE.Group();
    scene.add(pointsGroup);
    const markers = [];
    const list = (stateRef.current.cves || []).slice(0, 80);
    list.forEach((cve, i) => {
      const phi = Math.acos(-1 + (2 * i) / Math.max(list.length, 1));
      const theta = Math.sqrt(list.length * Math.PI) * phi;
      const r = 1.22 + (i % 3) * 0.08;
      const x = r * Math.cos(theta) * Math.sin(phi);
      const y = r * Math.cos(phi);
      const z = r * Math.sin(theta) * Math.sin(phi);
      const srcHex = sourceColor ? sourceColor(cve) : null;
      const colHex = cvssColor ? cvssColor(cve.cvss) : '#00ff88';
      const col = new THREE.Color(colHex);
      const ringCol = srcHex ? new THREE.Color(srcHex) : col;
      const m = new THREE.Mesh(
        new THREE.SphereGeometry(0.035, 8, 8),
        new THREE.MeshBasicMaterial({ color: col })
    );
      m.position.set(x, y, z);
      m.userData = { cve };
      pointsGroup.add(m);
      markers.push(m);
      if (sourceColor) {
        const ring = new THREE.Mesh(
          new THREE.SphereGeometry(0.055, 10, 10),
          new THREE.MeshBasicMaterial({ color: ringCol, transparent: true, opacity: 0.4, wireframe: true })
        );
        ring.position.set(x, y, z);
        ring.userData = { cve };
        pointsGroup.add(ring);
        markers.push(ring);
      }
      const lineGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(x, y, z).multiplyScalar(1.05 / r),
        new THREE.Vector3(x, y, z),
      ]);
      pointsGroup.add(new THREE.Line(lineGeo, new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.4 })));
      // label with CVE id
      const spr = makeCveLabel(cve.id || ('CVE-' + i), colHex);
      spr.position.set(x * 1.12, y * 1.12, z * 1.12);
      spr.userData = { cve };
      pointsGroup.add(spr);
      markers.push(spr);
    });

    // domain text via canvas texture on sprite
    const makeLabel = (text) => {
      const canvas = document.createElement('canvas');
      canvas.width = 256; canvas.height = 64;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'rgba(0,0,0,0)';
      ctx.fillRect(0, 0, 512, 128);
      ctx.font = 'bold 48px monospace';
      ctx.fillStyle = '#00ff88';
      ctx.textAlign = 'center';
      ctx.fillText(text.slice(0, 28), 256, 70);
      const tex = new THREE.CanvasTexture(canvas);
      const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
      const spr = new THREE.Sprite(mat);
    const list = (stateRef.current.cves || []).slice(0, 80);
      spr.scale.set(1.6, 0.4, 1);
      spr.position.set(0, -1.5, 0);
      return spr;
    };


    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const onClick = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      const hits = raycaster.intersectObjects(markers);
      if (hits.length && hits[0].object.userData.cve) {
        const cve = hits[0].object.userData.cve;
        if (stateRef.current.onSelect) stateRef.current.onSelect(cve);
      }
    };
    const onMove = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(markers);
      const cve = hits.length ? hits[0].object.userData.cve : null;
      if (stateRef.current.onHover) stateRef.current.onHover(cve);
    };
    renderer.domElement.addEventListener('click', onClick);
    renderer.domElement.addEventListener('mousemove', onMove);

    let frame = 0;
    let alive = true;
    const animate = () => {
      if (!alive) return;
      frame = requestAnimationFrame(animate);
      globe.rotation.y += 0.003;
      wire.rotation.y += 0.002;
      pointsGroup.rotation.y += 0.003;
      core.rotation.y += 0.01;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const nw = mount.clientWidth || w;
      const nh = mount.clientHeight || h;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      alive = false;
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('click', onClick);
      renderer.dispose();
      mount.innerHTML = '';
    };
  }, [domain, (cves || []).map(c => c.id).join(','), selectedId]);

  return <div ref={mountRef} style={{ width: '100%', height: '100%', minHeight: 400, borderRadius: 12, overflow: 'hidden' }} />;
}


function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [uiReady, setUiReady] = useState(false);
  const [bgVisible, setBgVisible] = useState(false);
  const [loginBgVisible, setLoginBgVisible] = useState(false);
  const [loginUiReady, setLoginUiReady] = useState(false);
  useEffect(() => {
    if (authenticated) {
      setLoginBgVisible(false);
      setLoginUiReady(false);
      return;
    }
    setLoginBgVisible(false);
    setLoginUiReady(false);
    const t1 = setTimeout(() => setLoginBgVisible(true), 40);
    const t2 = setTimeout(() => setLoginUiReady(true), 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [authenticated]);


  useEffect(() => {
    if (!authenticated) {
      setUiReady(false);
      setBgVisible(false);
      return;
    }
    const t1 = setTimeout(() => setBgVisible(true), 40);
    const t2 = setTimeout(() => setUiReady(true), 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [authenticated]);

  const [mainTab, setMainTab] = useState('guide');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const tabHas = (v) => {
    if (v == null || v === false) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') {
      if (v.error) return 'err';
      const keys = Object.keys(v);
      if (!keys.length) return false;
      return true;
    }
    return true;
  };
  const tabTone = (v) => {
    const h = tabHas(v);
    if (h === 'err') return 'err';
    return h ? 'ok' : 'empty';
  };

 // guide | scan | workplace
  const [workplaceResults, setWorkplaceResults] = useState(null);
  const [workplaceLoading, setWorkplaceLoading] = useState(false);
  const [workplaceDomain, setWorkplaceDomain] = useState('');
  const [leakEmail, setLeakEmail] = useState('');
  const [leakResults, setLeakResults] = useState(null);
  const [leakLoading, setLeakLoading] = useState(false);
  const [historyScans, setHistoryScans] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const [compareResult, setCompareResult] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [userRole, setUserRole] = useState('analyst');
  const [currentUser, setCurrentUser] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
    const [nucleiResults, setNucleiResults] = useState(null);
  const [waybackResults, setWaybackResults] = useState(null);
  const [dnssecResults, setDnssecResults] = useState(null);
  const [asnNeighborsResults, setAsnNeighborsResults] = useState(null);
  const [fullScanTimeline, setFullScanTimeline] = useState([]);
  const [ctResults, setCtResults] = useState(null);
  const [ctLoading, setCtLoading] = useState(false);


  const [authStep, setAuthStep] = useState('login');
  const [tempToken, setTempToken] = useState('');
  const [loginError, setLoginError] = useState('');
  const [token, setToken] = useState('');

  const [siteLog, setSiteLog] = useState([]);
  const [siteCommand, setSiteCommand] = useState('');
  const [siteActiveTab, setSiteActiveTab] = useState('overview');
  window.setActiveTab = setSiteActiveTab;
  const [reportData, setReportData] = useState(null);
  const [shodanData, setShodanData] = useState(null);
  const [dirResults, setDirResults] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [dnsResults, setDnsResults] = useState(null);
  const [sslResults, setSslResults] = useState(null);
  const [jsResults, setJsResults] = useState(null);
  const [geoResults, setGeoResults] = useState(null);
  const [vhostResults, setVhostResults] = useState(null);
  const [robotsResults, setRobotsResults] = useState(null);
  const [adminResults, setAdminResults] = useState(null);
  const [cloudResults, setCloudResults] = useState(null);
  const [secHeadersResults, setSecHeadersResults] = useState(null);
  const [emailResults, setEmailResults] = useState(null);
  const [gitResults, setGitResults] = useState(null);
  const [whatwebResults, setWhatwebResults] = useState(null);
  const [cveResults, setCveResults] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [stealthScanResults, setStealthScanResults] = useState(null);
  const [stealthScanning, setStealthScanning] = useState(false);
  const [selectedCve, setSelectedCve] = useState(null);
  const [lang, setLang] = useState(localStorage.getItem('darkai_lang') || 'ru');
  const [cveCvssFilter, setCveCvssFilter] = useState('all');
const [cveSourceFilter, setCveSourceFilter] = useState('all'); // all|shodan|nuclei|tech
  const [baseReconBusy, setBaseReconBusy] = useState(false);
  const [uiAccent, setUiAccent] = useState(() => localStorage.getItem('darkai_accent') || 'cyan');
  const [graphHighlightLane, setGraphHighlightLane] = useState(null);
  const [globeTipCve, setGlobeTipCve] = useState(null);

 // all|critical|high|medium|low
  const [corsResults, setCorsResults] = useState(null);
  const [emailDnsResults, setEmailDnsResults] = useState(null);
  const [asnResults, setAsnResults] = useState(null);
  const [relatedResults, setRelatedResults] = useState(null);
  const [liveProbeResults, setLiveProbeResults] = useState(null);
  const [subSort, setSubSort] = useState('all'); // all|with_ip|no_ip|live
  const [fullScanning, setFullScanning] = useState(false);
  const [fullScanDone, setFullScanDone] = useState(false);
  const [riskIndex, setRiskIndex] = useState(null);
  const scanSnapRef = useRef({}); /* FIX_SCORE_GLOBE_HDR */

  const [ptrResults, setPtrResults] = useState(null);
  const [passiveDnsResults, setPassiveDnsResults] = useState(null);
  const [mxResults, setMxResults] = useState(null);
  const [ipv6Results, setIpv6Results] = useState(null);
  const [graphqlResults, setGraphqlResults] = useState(null);
  const [sitemapParamsResults, setSitemapParamsResults] = useState(null);
  const [versionCommentsResults, setVersionCommentsResults] = useState(null);
  const [mobileApiResults, setMobileApiResults] = useState(null);
  const [websocketResults, setWebsocketResults] = useState(null);
  const [tenantResults, setTenantResults] = useState(null);
  const [cloudBucketsResults, setCloudBucketsResults] = useState(null);
  const [thirdPartyResults, setThirdPartyResults] = useState(null);
  const [typosquatResults, setTyposquatResults] = useState(null);
  const [wafResults, setWafResults] = useState(null);
  const [githubResults, setGithubResults] = useState(null);
  /* SPOOF_TAB_REMOVED */
  const abortRef = useRef(null);




  useEffect(() => {
    if (!authenticated || !fullScanDone || !reportData?.target) return;
    const id = setTimeout(() => {
      if (typeof refreshRiskIndex === 'function') refreshRiskIndex({ fullScan: true });
    }, 800);
    return () => clearTimeout(id);
  }, [fullScanDone, reportData?.target, authenticated]);

  useEffect(() => {
    const themes = {
      cyan: { main: '#00ff88', dim: 'rgba(0,255,136,0.28)', soft: 'rgba(0,255,136,0.12)', glow: 'rgba(0,255,136,0.35)' },
      amber: { main: '#ffcc44', dim: 'rgba(255,204,68,0.35)', soft: 'rgba(255,204,68,0.14)', glow: 'rgba(255,204,68,0.4)' },
      purple: { main: '#c084fc', dim: 'rgba(192,132,252,0.35)', soft: 'rgba(192,132,252,0.14)', glow: 'rgba(192,132,252,0.38)' },
    };
    const th = themes[uiAccent] || themes.cyan;
    const root = document.documentElement;
    root.style.setProperty('--da-accent', th.main);
    root.style.setProperty('--da-accent-dim', th.dim);
    root.style.setProperty('--da-accent-soft', th.soft);
    root.style.setProperty('--da-accent-glow', th.glow);
    localStorage.setItem('darkai_accent', uiAccent);
  }, [uiAccent]);

  useEffect(() => {
    const savedToken = localStorage.getItem('darkai_token');
    if (!savedToken) return;
    (async () => {
      try {
        const res = await fetch('/api/me', { headers: { Authorization: `Bearer ${savedToken}` } });
        if (!res.ok) {
          localStorage.removeItem('darkai_token');
          setLoginError(t('Сессия истекла — войдите снова (логин + 2FA)', 'Session expired — log in again'));
          return;
        }
        const data = await res.json();
        setToken(savedToken);
        setAuthenticated(true);
        setCurrentUser(data.user || '');
        setUserRole(data.role || 'analyst');
      } catch (e) {
        localStorage.removeItem('darkai_token');
      }
    })();
  }, []);


  const authToken = () => (token || localStorage.getItem('darkai_token') || '').trim();

  const clearSession = (msg) => {
    setToken('');
    localStorage.removeItem('darkai_token');
    setAuthenticated(false);
    setAuthStep('login');
    setTempToken('');
    setLoginError(msg || '');
    setReportData(null);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    const form = new FormData(e.target);
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.get('username'),
          password: form.get('password')
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLoginError(data.detail || data.error || 'Invalid credentials');
        return;
      }
      if (data.require_2fa) {
        setTempToken(data.temp_token);
        setAuthStep('2fa');
        setLoginError('');
      } else if (data.access_token) {
        setToken(data.access_token);
        localStorage.setItem('darkai_token', data.access_token);
        setAuthenticated(true);
      } else {
        setLoginError('Login failed');
      }
    } catch (err) {
      setLoginError('Network error');
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const totp_code = form.get('totp');
    try {
      const res = await fetch('/api/verify-2fa', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ temp_token: tempToken, totp_code }) });
      const data = await res.json();
      if (data.access_token) {
        setToken(data.access_token); localStorage.setItem('darkai_token', data.access_token); setAuthenticated(true); setLoginError('');
      } else { setLoginError(data.detail || 'Invalid 2FA code'); }
    } catch (err) { setLoginError('Network error'); }
  };

  const handleLogout = () => {
    setToken('');
    localStorage.removeItem('darkai_token');
    setAuthenticated(false);
    setAuthStep('login');
    setTempToken('');
    setLoginError('');
    setReportData(null);
    setSiteLog([]);
    setUserRole('analyst');
    setCurrentUser('');
    setMainTab('guide');
    setFullScanDone(false);
  };


  const netMsg = (err) => (
    err?.name === "AbortError"
      ? "Таймаут 3 мин — повторите или укоротите цель."
      : (String(err?.message || err).includes("NetworkError")
        ? "Сеть оборвалась — обновите страницу (Ctrl+Shift+R) и повторите."
        : (err?.message || String(err)))
  );

  const normalizeTargetInput = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    let s = raw.trim();
    s = s.replace(/^https?:\/\//i, '').replace(/^\/\//, '');
    s = s.split('/')[0].split('?')[0].split('#')[0].trim();
    s = s.replace(/:\d+$/, '');
    return s;
  };

  const lastSubmittedTarget = () => {
    for (let i = siteLog.length - 1; i >= 0; i--) {
      const line = siteLog[i];
      if (typeof line === 'string' && line.startsWith('➜ ')) {
        return normalizeTargetInput(line.slice(2));
      }
    }
    return '';
  };


  const cveSourceBucket = (c) => {
    const parts = [...(c?.sources || []), c?.source].filter(Boolean).map(x => String(x).toLowerCase());
    const s = parts.join(' ');
    if (s.includes('shodan')) return 'shodan';
    if (s.includes('nuclei')) return 'nuclei';
    if (s.includes('nvd') || s.includes('vulners') || s.includes('tech') || s.includes('circl')) return 'tech';
    return 'other';
  };

  const filterCvesBySource = (list) => {
    if (!cveSourceFilter || cveSourceFilter === 'all') return list;
    if (cveSourceFilter === 'tech') {
      return list.filter(c => cveSourceBucket(c) === 'tech' || cveSourceBucket(c) === 'other');
    }
    return list.filter(c => cveSourceBucket(c) === cveSourceFilter);
  };

  const formatDnsDiscoveryLog = (data) => {
    const raw = data?.raw_data || {};
    const dd = raw.dns_discovery || {};
    const parts = Object.entries(dd).filter(([, v]) => v).map(([k, v]) => `${k}:${v}`).join(', ');
    const subs = (raw.subdomains || []).length;
    const resolved = raw.dns_active_resolved ?? subs;
    return `[DNS] passive/enum → ${subs} имён, резолв A: ${resolved}${parts ? ` · источники (${parts})` : ''}`;
  };

  const formatTlsLog = (ssl) => {
    if (!ssl || typeof ssl !== 'object') return '[TLS] нет данных';
    if (ssl.error) return `[TLS] ${ssl.error}`;
    const iss = (ssl.issuer || '').includes('CN=') ? (ssl.issuer || '').split('CN=').pop().slice(0, 48) : (ssl.issuer || '').slice(0, 48);
    const san = (ssl.san || []).length;
    const days = ssl.days_left != null ? ssl.days_left : '?';
    return `[TLS] ${ssl.host || ''} · issuer: ${iss || '—'} · SAN: ${san} · осталось ${days} дн.`;
  };



  /* DASHBOARD_UI_V1 */
  const cveSourceLegendColor = (c) => {
    const b = cveSourceBucket(c);
    if (b === 'shodan') return '#66ccff';
    if (b === 'nuclei') return '#ffaa44';
    if (b === 'tech') return '#cc88ff';
    return '#88ffcc';
  };

  const dashboardStats = () => {
    if (!reportData) return null;
    const raw = reportData.raw_data || {};
    const subs = (raw.subdomains || []).length;
    const cveN = typeof getMergedCveList === 'function'
      ? getMergedCveList(cveResults?.cves || []).length
      : (cveResults?.cves || []).length;
    return { target: reportData.target, subs, cveN };
  };

  const renderAccentPicker = () => (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <span style={{ fontSize: 10, opacity: 0.55, letterSpacing: 1 }}>ТЕМА</span>
      {[['cyan', '#00ff88'], ['amber', '#ffcc44'], ['purple', '#c084fc']].map(([k, col]) => (
        <button key={k} type="button" onClick={() => setUiAccent(k)} title={k}
          style={{
            width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', padding: 0,
            border: uiAccent === k ? '2px solid #fff' : '1px solid rgba(255,255,255,0.25)',
            background: col, boxShadow: uiAccent === k ? '0 0 14px var(--da-accent-glow)' : 'none',
          }} />
      ))}
    </div>
  );

  const renderDashboardHeader = () => {
    const st = dashboardStats();
    if (!st) return renderAccentPicker();
    return (
      <DashboardBar>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', flex: 1 }}>
          <span style={{ fontSize: 11, letterSpacing: 2, opacity: 0.65 }}>DASHBOARD</span>
          <StatChip><b>{st.target}</b></StatChip>
          <StatChip>subs <b>{st.subs}</b></StatChip>
          <StatChip>CVE <b>{st.cveN}</b></StatChip>
          <StatChip>{fullScanning ? 'FULL…' : fullScanDone ? 'FULL ✓' : 'base recon'}</StatChip>
          {typeof renderRiskGauge === 'function' ? renderRiskGauge(true) : null}
        </div>
        {renderAccentPicker()}
      </DashboardBar>
    );
  };

  const renderScanEmptyHero = () => (
    <EmptyHero>
      <div style={{ fontSize: 46, marginBottom: 10 }}>🛰</div>
      <h3 style={{ margin: '0 0 10px', color: 'var(--da-accent)', letterSpacing: 2, fontSize: 18 }}>Панель разведки</h3>
      <p style={{ opacity: 0.78, lineHeight: 1.65, maxWidth: 540, margin: '0 auto 16px', fontSize: 14 }}>
        Отправьте домен или IP — справа появится глобус CVE, сверху статистика, FULL SCAN даст пошаговый прогресс.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', fontSize: 12, opacity: 0.7 }}>
        <span>📋 Обзор</span><span>🌐 Globe</span><span>📊 Граф</span><span>📄 PDF</span>
      </div>
    </EmptyHero>
  );

  const logLineStyle = (line) => {
    const s = String(line || '');
    const base = {
      whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, lineHeight: 1.55,
      padding: '7px 11px', marginBottom: 5, borderRadius: 8,
      background: 'rgba(0, 10, 8, 0.45)', borderLeft: '3px solid rgba(0,255,136,0.25)',
    };
    if (s.startsWith('[ERROR]') || s.includes('Invalid authentication')) {
      return { ...base, color: '#ff8899', borderLeftColor: '#ff4466', background: 'rgba(40,0,8,0.35)' };
    }
    if (s.startsWith('[FULL]')) return { ...base, color: '#ffe088', borderLeftColor: '#ffcc44' };
    if (s.startsWith('[RECON]') || s.includes('Базовая разведка')) {
      return { ...base, color: '#ffdd77', borderLeftColor: '#ffaa22', background: 'rgba(30,25,0,0.25)' };
    }
    if (s.startsWith('[DNS]') || s.includes('[DNS ')) return { ...base, color: '#77ddff', borderLeftColor: '#00aacc' };
    if (s.includes('[TLS]') || s.startsWith('[FULL][TLS]')) return { ...base, color: '#99ccff', borderLeftColor: '#5588dd' };
    if (s.startsWith('➜')) return { ...base, color: '#00ffaa', fontWeight: 700, borderLeftColor: '#00ff88', fontSize: 14 };
    return { ...base, color: '#a8e8c8', opacity: 0.92 };
  };

  const runBaseRecon = async (target, opts = {}) => {
    const cmd = normalizeTargetInput(target);
    if (!cmd) throw new Error(t('Пустая цель', 'Empty target'));
    if (!opts.skipLogLine) setSiteLog(prev => [...prev, `➜ ${cmd}`]);
    setBaseReconBusy(true);
    setSiteLog(prev => [...prev, t('[RECON] Базовая разведка… (до 3 мин)', '[RECON] Base recon running…')]);
    const ctrl = new AbortController();
    const tmo = setTimeout(() => ctrl.abort(), 180000);
    try {
      const res = await fetch('/api/execute', {
        method: 'POST', signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken()}`, 'X-API-KEY': 'kDfN3j' },
        body: JSON.stringify({ target: cmd }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail || data.error || res.statusText || `HTTP ${res.status}`;
        if (res.status === 403 || res.status === 401 || String(detail).includes('Invalid authentication')) {
          clearSession(t('Сессия истекла — войдите снова (логин + 2FA)', 'Session expired — log in again'));
        }
        throw new Error(detail);
      }
      if (data.error) throw new Error(data.error);
      if (data.type !== 'domain') {
        throw new Error(t(`Тип цели: ${data.type || '?'} — FULL SCAN только для домена`, `Target type ${data.type || '?'} — domain only`));
      }
      setReportData(data);
      setSiteLog(prev => [...prev, formatDnsDiscoveryLog(data)]);
      setSiteActiveTab('overview');
      if (typeof refreshRiskIndex === 'function') refreshRiskIndex({ fullScan: false });
      setShodanData(null); setDirResults(null); setDnsResults(null); setSslResults(null);
      setJsResults(null); setGeoResults(null); setVhostResults(null); setRobotsResults(null);
      setAdminResults(null); setCloudResults(null); setSecHeadersResults(null); setEmailResults(null);
      setGitResults(null); setWhatwebResults(null); setCveResults(null); setStealthScanResults(null);
      setNucleiResults(null); setWaybackResults(null); setDnssecResults(null); setAsnNeighborsResults(null);
      setFullScanTimeline([]);
      setSelectedCve(null);
      setFullScanDone(false);
      setLiveProbeResults(null);
      setRelatedResults(null);
      setAsnResults(null);
      setEmailDnsResults(null);
      setCorsResults(null);
      return data;
    } finally {
      clearTimeout(tmo);
      setBaseReconBusy(false);
    }
  };

  const siteExecute = async (e) => {
    e.preventDefault();
    const cmd = normalizeTargetInput(siteCommand);
    if (!cmd) return;
    setSiteCommand('');
    try {
      await runBaseRecon(cmd);
    } catch (err) {
      const msg = typeof netMsg === 'function' ? netMsg(err) : (err?.message || String(err));
      setSiteLog(prev => [...prev, `[ERROR] ${msg}`]);
    }
  };

  const extractShodanCvesDeep = (obj, acc = [], seen = new Set()) => {
    if (!obj || typeof obj !== 'object') return acc;
    if (Array.isArray(obj)) {
      obj.forEach(x => extractShodanCvesDeep(x, acc, seen));
      return acc;
    }
    // vulns as object map or array
    if (obj.vulns) {
      if (Array.isArray(obj.vulns)) {
        obj.vulns.forEach(v => {
          const id = typeof v === 'string' ? v : (v?.id || v?.cve || v?.cve_id);
          if (id && String(id).toUpperCase().startsWith('CVE') && !seen.has(id)) {
            seen.add(id);
            acc.push({
              id: String(id),
              title: (typeof v === 'object' && (v.title || v.summary)) || String(id),
              description: (typeof v === 'object' && (v.summary || v.description || v.cvss_version)) || 'Найдено в Shodan',
              cvss: (typeof v === 'object' && (v.cvss || v.cvss3 || v.cvss_v3 || v.cvss_score)) || null,
              source: 'shodan'
            });
          }
        });
      } else if (typeof obj.vulns === 'object') {
        Object.entries(obj.vulns).forEach(([id, v]) => {
          if (String(id).toUpperCase().startsWith('CVE') && !seen.has(id)) {
            seen.add(id);
            acc.push({
              id: String(id),
              title: (v && (v.title || v.summary)) || id,
              description: (v && (v.summary || v.description)) || 'Найдено в Shodan',
              cvss: (v && (v.cvss || v.cvss3 || v.cvss_v3)) || null,
              source: 'shodan'
            });
          }
        });
      }
    }
    // fields like cve, cve_id, vulnerability
    for (const [k, v] of Object.entries(obj)) {
      if (/^cve/i.test(k) && typeof v === 'string' && v.toUpperCase().startsWith('CVE') && !seen.has(v)) {
        seen.add(v);
        acc.push({ id: v, title: v, description: 'Найдено в Shodan', cvss: null, source: 'shodan' });
      }
      if (v && typeof v === 'object' && k !== 'vulns') extractShodanCvesDeep(v, acc, seen);
    }
    return acc;
  };

  const mergeShodanCves = (shodanPayload) => {
    const found = extractShodanCvesDeep(shodanPayload);
    if (!found.length) {
 return;
    }
    setCveResults(prev => {
      const existing = prev?.cves || [];
      const seen = new Set(existing.map(c => c.id));
      const merged = [...existing];
      found.forEach(c => { if (!seen.has(c.id)) { seen.add(c.id); merged.push(c); } });
      return { ...(prev || {}), cves: merged, query: (prev?.query || '') + ' +shodan' };
    });
  };

  const fetchShodan = async (ip) => {
    if (!ip || ip === '127.0.0.1') return;
    try {
      const res = await fetch(`/api/shodan/${ip}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      const payload = data.structured || data.shodan || data.data || data;
      setShodanData(payload);
      mergeShodanCves(payload);
        scanSnapRef.current.shodan = payload;
      setSiteActiveTab('shodan');
    } catch (err) { setSiteLog(prev => [...prev, `[Shodan] Ошибка: ${err.message}`]); }
  };

  const scanDirs = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    setScanning(true);
    try {
      const res = await fetch('/api/scan-dirs', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setDirResults(data.directories); setSiteActiveTab('dirs');
    } catch (err) { setSiteLog(prev => [...prev, `[ERROR] Сканирование директорий: ${err.message}`]); }
    setScanning(false);
  };

  const runDnsAudit = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/dns-audit', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setDnsResults(data.dns_records);
      setSiteActiveTab('dns');
    } catch (err) { setSiteLog(prev => [...prev, `[DNS] Ошибка: ${err.message}`]); }
  };

  const runSslAnalysis = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/ssl-analysis', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setSslResults(data.ssl_data);
      setSiteActiveTab('ssl');
    } catch (err) { setSiteLog(prev => [...prev, `[SSL] Ошибка: ${err.message}`]); }
  };

  const runJsAnalysis = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/js-analysis', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setJsResults(data.js_data);
      setSiteActiveTab('js');
    } catch (err) { setSiteLog(prev => [...prev, `[JS] Ошибка: ${err.message}`]); }
  };

  const runGeoIP = async (ip) => {
    if (!ip || ip === '127.0.0.1') return;
    try {
      const res = await fetch('/api/geo-ip', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: ip }) });
      const data = await res.json();
      setGeoResults(data.geo);
      setSiteActiveTab('geo');
    } catch (err) { setSiteLog(prev => [...prev, `[Geo] Ошибка: ${err.message}`]); }
  };

  const runVhostBruteforce = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/vhost-bruteforce', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setVhostResults(data.vhost_data);
      setSiteActiveTab('vhost');
    } catch (err) { setSiteLog(prev => [...prev, `[VHOST] Ошибка: ${err.message}`]); }
  };

  const runRobotsSitemap = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/robots-sitemap', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setRobotsResults(data.data);
      setSiteActiveTab('robots');
    } catch (err) { setSiteLog(prev => [...prev, `[Robots] Ошибка: ${err.message}`]); }
  };

  const runAdminScan = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/admin-scan', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setAdminResults(data.found);
      setSiteActiveTab('admin');
    } catch (err) { setSiteLog(prev => [...prev, `[Admin] Ошибка: ${err.message}`]); }
  };

  const runCloudStorage = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/cloud-storage', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setCloudResults(data.storages);
      setSiteActiveTab('cloud');
    } catch (err) { setSiteLog(prev => [...prev, `[Cloud] Ошибка: ${err.message}`]); }
  };

  const runSecurityHeaders = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/security-headers', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setSecHeadersResults(data.headers_data);
      setSiteActiveTab('security');
    } catch (err) { setSiteLog(prev => [...prev, `[Security Headers] Ошибка: ${err.message}`]); }
  };

  const runEmailHarvest = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/email-harvest', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setEmailResults(data.emails);
      setSiteActiveTab('emails');
    } catch (err) { setSiteLog(prev => [...prev, `[Email] Ошибка: ${err.message}`]); }
  };

  const runGitBackupScan = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/git-backup-scan', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setGitResults(data.sensitive_files);
      setSiteActiveTab('git');
    } catch (err) { setSiteLog(prev => [...prev, `[Git] Ошибка: ${err.message}`]); }
  };

  const runWhatWeb = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    try {
      const res = await fetch('/api/whatweb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' },
        body: JSON.stringify({ target: reportData.target })
      });
      const data = await res.json();
      setWhatwebResults(data.whatweb_data);
      setSiteActiveTab('whatweb');
    } catch (err) {
      setSiteLog(prev => [...prev, `[WhatWeb] Ошибка: ${err.message}`]);
    }
  };


  const getMergedCveList = (apiList = []) => {
    const merged = [...(apiList || [])];
    const seen = new Set(merged.map(c => c.id).filter(Boolean));
    extractShodanCvesDeep(shodanData, merged, seen);
    return merged;
  };

  const refreshAutoCves = async (logLabel = 'AUTO') => {
    if (!reportData) return;
    try {
      const extra = typeof buildReportExtras === 'function' ? buildReportExtras() : { ...(typeof buildExtraModules === 'function' ? buildExtraModules() : {}), ...(scanSnapRef.current || {}) };
      const res = await fetch('/api/cve/auto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken()}` },
        body: JSON.stringify({
          target: reportData.target,
          report_data: reportData,
          extra_modules: extra,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const apiList = data.cves || data.cve_data?.cves || [];
      const src = data.cve_data?.sources || {};
      const shodanOnly = extractShodanCvesDeep(shodanData || {}, [], new Set());
      const merged = getMergedCveList(apiList);
      setCveResults({ cves: merged, query: 'auto', source: 'auto', sources: src });
      const srcTxt = Object.entries(src).map(([k, v]) => k + ':' + v).join(', ') || 'модули';
      setSiteLog(prev => [...prev, `[${logLabel}] CVE: API ${apiList.length}, Shodan ${shodanOnly.length}, на глобусе ${merged.length} (${srcTxt})`]);
    } catch (err) {
      setSiteLog(prev => [...prev, `[${logLabel}] CVE auto: ${err.message}`]);
    }
  };

  const runCveSearch = async () => {
    await refreshAutoCves('CVE');
    setSiteActiveTab('overview');
  };


const runStealthScan = async () => {
    if (!reportData || reportData.type !== 'domain') return;
    setStealthScanning(true);
    setSiteLog(prev => [...prev, `[StealthScan] Запуск скрытого сканирования портов... (может занять 2–5 минут)`]);
    try {
      const res = await fetch('/api/stealth-scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' },
        body: JSON.stringify({ target: reportData.target })
      });
      const data = await res.json();
      if (res.status === 429) {
        setSiteLog(prev => [...prev, `[StealthScan] ${data.detail || 'Сканирование уже выполняется'}`]);
      } else {
        setStealthScanResults(data.stealth_scan);
        setSiteActiveTab('stealth');
        setSiteLog(prev => [...prev, `[StealthScan] Готово`]);
      }
    } catch (err) {
      setSiteLog(prev => [...prev, `[StealthScan] Ошибка: ${err.message}`]);
    }
    setStealthScanning(false);
  };
  const postApi = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify(body),
      signal: abortRef.current && abortRef.current.signal
    });
    return res.json();
  };
  const runPtrSubnet = async (ip) => {
    const target = ip || reportData?.target;
    if (!target) return;
    try {
      const d = await postApi('/api/ptr-subnet', { target });
      setPtrResults(d.data); setSiteActiveTab('ptr');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[PTR] ' + e.message]); }
  };
  const runPassiveDns = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/passive-dns', { target: reportData.target });
      setPassiveDnsResults(d.data); setSiteActiveTab('pdns');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[PDNS] ' + e.message]); }
  };
  const runMxAudit = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/mx-audit', { target: reportData.target });
      setMxResults(d.data); setSiteActiveTab('mx');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[MX] ' + e.message]); }
  };
  const runIpv6 = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/ipv6', { target: reportData.target });
      setIpv6Results(d.data); setSiteActiveTab('ipv6');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[IPv6] ' + e.message]); }
  };
  const runGraphql = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/graphql', { target: reportData.target });
      setGraphqlResults(d.data); setSiteActiveTab('graphql');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[GQL] ' + e.message]); }
  };
  const runSitemapParams = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/sitemap-params', { target: reportData.target });
      setSitemapParamsResults(d.data); setSiteActiveTab('sitemapparams');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[Sitemap] ' + e.message]); }
  };
  const runVersionComments = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/version-comments', { target: reportData.target });
      setVersionCommentsResults(d.data); setSiteActiveTab('versions');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[Ver] ' + e.message]); }
  };
  const runMobileApi = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/mobile-api', { target: reportData.target });
      setMobileApiResults(d.data); setSiteActiveTab('mobileapi');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[Mobile] ' + e.message]); }
  };
  const runWebsocket = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/websocket', { target: reportData.target });
      setWebsocketResults(d.data); setSiteActiveTab('websocket');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[WS] ' + e.message]); }
  };
  const runTenantEnum = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/tenant-enum', { target: reportData.target });
      setTenantResults(d.data); setSiteActiveTab('tenant');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[Tenant] ' + e.message]); }
  };
  const runCloudBuckets = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/cloud-buckets', { target: reportData.target });
      setCloudBucketsResults(d.data); setSiteActiveTab('buckets');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[Buckets] ' + e.message]); }
  };
  const runThirdParty = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/third-party', { target: reportData.target });
      setThirdPartyResults(d.data); setSiteActiveTab('thirdparty');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[3P] ' + e.message]); }
  };
  const runTyposquat = async () => {
    if (!reportData) return;
    try {
      const d = await postApi('/api/typosquat', { target: reportData.target });
      setTyposquatResults(d.data); setSiteActiveTab('typosquat');
    } catch (e) { if (e.name !== 'AbortError') setSiteLog(prev => [...prev, '[Typo] ' + e.message]); }
  };
  
  const renderFullScanTimeline = () => {
    if (!fullScanTimeline.length) return null;
    const done = fullScanTimeline.filter(s => s.status === 'done').length;
    const running = fullScanTimeline.find(s => s.status === 'running');
    const total = fullScanTimeline.length;
    const pct = total ? Math.round((done / total) * 100) : 0;
    return (
      <div style={{
        marginTop: 12, marginBottom: 12, padding: '14px 16px',
        border: '1px solid var(--da-accent-dim)', borderRadius: 12,
        background: 'rgba(0,12,8,0.6)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div style={{ color: 'var(--da-accent)', fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
            FULL SCAN · {done}/{total}
          </div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>{pct}%{running ? ` · ▶ ${running.name}` : ''}</div>
        </div>
        <div style={{ height: 8, background: 'rgba(0,40,30,0.85)', borderRadius: 6, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`, height: '100%',
            background: 'linear-gradient(90deg, var(--da-accent), #00ccaa)',
            borderRadius: 6, transition: 'width 0.35s ease',
            boxShadow: '0 0 12px var(--da-accent-glow)',
          }} />
        </div>
        <StepperTrack title="Шаги FULL SCAN">
          {fullScanTimeline.map((s, i) => (
            <StepDot key={i} $done={s.status === 'done'} $run={s.status === 'running'} $err={s.status === 'error'} />
          ))}
        </StepperTrack>
        <div style={{ maxHeight: 160, overflowY: 'auto', paddingRight: 4 }}>
          {fullScanTimeline.map((s, i) => (
            <div key={i} style={{
              fontSize: 11, padding: '3px 0', color:
                s.status === 'done' ? '#66ffaa' :
                s.status === 'error' ? '#ff6688' :
                s.status === 'running' ? '#ffcc00' : '#777',
              animation: s.status === 'running' ? 'logFadeIn 0.4s ease' : undefined,
            }}>
              {s.status === 'done' ? '✓' : s.status === 'error' ? '✗' : s.status === 'running' ? '▶' : '○'} {s.name}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const runFullScan = async () => {
    let activeReport = (reportData && reportData.type === 'domain') ? reportData : null;
    if (!activeReport) {
      const pending = normalizeTargetInput(siteCommand) || lastSubmittedTarget();
      if (pending) {
        setSiteLog(prev => [...prev, `[FULL] ${t('Дожидаемся базовой разведки…','Running base recon first')}: ${pending}`]);
        try {
          activeReport = await runBaseRecon(pending, { skipLogLine: !!lastSubmittedTarget() });
        } catch (err) {
          setSiteLog(prev => [...prev, `[FULL] ${typeof netMsg === 'function' ? netMsg(err) : err.message}`]);
          return;
        }
      }
    }
    if (!activeReport || activeReport.type !== 'domain') {
      setSiteLog(prev => [...prev, `[FULL] ${t('Сначала отправьте домен (ОТПРАВИТЬ) и дождитесь обзора','Submit domain with SEND and wait for overview')}`]);
      return;
    }
    setReportData(activeReport);
    const target = activeReport.target;
    const ipsMap = reportData.raw_data?.subdomain_ips || {};
    const mainIP = ipsMap[target]?.[0] || Object.values(ipsMap).flat().find(Boolean);
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
    const post = async (url, body) => {
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      return res.json().catch(() => ({ error: 'bad json' }));
    };

    setFullScanning(true);
    setSiteLog(prev => [...prev, `[FULL] ${t('Старт полного сканирования','Full scan started')}: ${target}`]);
    scanSnapRef.current = {};

    const steps = [
      ['WhatWeb', async () => {
        const d = await post('/api/whatweb', { target });
        setWhatwebResults(d.whatweb_data);
        scanSnapRef.current.whatweb = d.whatweb_data;
      }],
      ['DNS active (записи)', async () => {
        const d = await post('/api/dns-audit', { target });
        setDnsResults(d.dns_records);
        const rec = d.dns_records || {};
        const n = typeof rec === 'object' ? Object.keys(rec).length : 0;
        setSiteLog(prev => [...prev, `[FULL][DNS active] типов записей: ${n}`]);
      }],
      ['DNS passive (PDNS)', async () => {
        const d = await post('/api/passive-dns', { target });
        setPassiveDnsResults(d.data);
        const rows = d.data?.passive_dns || d.data?.records || d.data || [];
        const cn = Array.isArray(rows) ? rows.length : (typeof rows === 'object' ? Object.keys(rows).length : 0);
        setSiteLog(prev => [...prev, `[FULL][DNS passive] записей PDNS: ${cn}`]);
      }],
      ['DNSSEC', async () => {
        const d = await post('/api/dnssec-walk', { target });
        setDnssecResults(d.dnssec);
      }],
      ['CT (crt.sh)', async () => {
        const d = await post('/api/ct-logs', { target });
        setCtResults(d.ct || d);
        const subs = (d.ct || d).subdomains || [];
        if (subs.length) {
          setReportData(prev => {
            if (!prev) return prev;
            const existing = new Set(prev.raw_data?.subdomains || []);
            subs.forEach(s => existing.add(s));
            return { ...prev, raw_data: { ...(prev.raw_data || {}), subdomains: Array.from(existing).sort() } };
          });
        }
      }],
      ['SSL / TLS', async () => {
        const d = await post('/api/ssl-analysis', { target });
        const ssl = d.ssl_data || {};
        setSslResults(ssl);
        scanSnapRef.current.ssl = ssl;
        setSiteLog(prev => [...prev, `[FULL] ${formatTlsLog(ssl)}`]);
      }],
      ['JS', async () => {
        const d = await post('/api/js-analysis', { target });
        setJsResults(d.js_data);
      }],
      ['Robots', async () => {
        const d = await post('/api/robots-sitemap', { target });
        setRobotsResults(d.data);
      }],
      ['Wayback URLs', async () => {
        const d = await post('/api/url-history', { target });
        setWaybackResults(d.wayback);
      }],
      ['Dirs', async () => {
        const d = await post('/api/scan-dirs', { target });
        setDirResults(d.directories); scanSnapRef.current.dirs = d.directories;
      }],
      ['Admin', async () => {
        const d = await post('/api/admin-scan', { target });
        setAdminResults(d.found);
        scanSnapRef.current.admin = d.found;
      }],
      ['Cloud', async () => {
        const d = await post('/api/cloud-storage', { target });
        setCloudResults(d.storages);
      }],
      ['Headers', async () => {
        const d = await post('/api/security-headers', { target });
        setSecHeadersResults(d.headers_data);
        scanSnapRef.current.security_headers = d.headers_data;
      }],
      ['Email', async () => {
        const d = await post('/api/email-harvest', { target });
        setEmailResults(d.emails);
      }],
      ['Git', async () => {
        const d = await post('/api/git-backup-scan', { target });
        setGitResults(d.sensitive_files);
      }],
      ['VHost', async () => {
        const d = await post('/api/vhost-bruteforce', { target });
        setVhostResults(d.vhost_data);
      }],
      ['CORS', async () => {
        const d = await post('/api/cors-redirect', { target });
        setCorsResults(d.data);
      }],
      ['SPF/DKIM', async () => {
        const d = await post('/api/email-dns', { target });
        setEmailDnsResults(d.data);
      }],
      ['Related', async () => {
        const d = await post('/api/related-domains', { target });
        setRelatedResults(d.data);
      }],
      ['Live-probe', async () => {
        const subs = reportData.raw_data?.subdomains || [target];
        const d = await post('/api/live-probe', { subdomains: subs });
        setLiveProbeResults(d);
      }],
      ['Nuclei', async () => {
        const urls = [];
        const lp = liveProbeResults;
        (lp?.live || lp?.urls || []).forEach(u => { if (u && !urls.includes(u)) urls.push(u); });
        if (!urls.length) urls.push(`https://${target}`, `http://${target}`);
        const d = await post('/api/nuclei-scan', { target, urls: urls.slice(0, 20) });
        setNucleiResults(d.nuclei);
        scanSnapRef.current.nuclei = d.nuclei;
      }],
    ];

    if (mainIP) {
      steps.push(['Shodan', async () => {
        const res = await fetch(`/api/shodan/${mainIP}`, { headers: { 'Authorization': `Bearer ${authToken()}` } });
        const d = await res.json();
        const payload = d.structured || d;
        setShodanData(payload);
        mergeShodanCves(payload);
      }]);
      steps.push(['GeoIP', async () => {
        const d = await post('/api/geo-ip', { target: mainIP });
        setGeoResults(d.geo);
      }]);
      steps.push(['ASN', async () => {
        const d = await post('/api/asn-lookup', { target: mainIP });
        setAsnResults(d.data);
      }]);
    }

    steps.push(['MX audit', async () => { const d = await post('/api/mx-audit', { target }); setMxResults(d.data); }]);
    steps.push(['IPv6', async () => { const d = await post('/api/ipv6', { target }); setIpv6Results(d.data); }]);
    steps.push(['GraphQL', async () => { const d = await post('/api/graphql', { target }); setGraphqlResults(d.data); }]);
    steps.push(['Sitemap params', async () => { const d = await post('/api/sitemap-params', { target }); setSitemapParamsResults(d.data); }]);
    steps.push(['Versions', async () => { const d = await post('/api/version-comments', { target }); setVersionCommentsResults(d.data); }]);
    steps.push(['Mobile API', async () => { const d = await post('/api/mobile-api', { target }); setMobileApiResults(d.data); }]);
    steps.push(['WebSocket', async () => { const d = await post('/api/websocket', { target }); setWebsocketResults(d.data); }]);
    steps.push(['Tenant', async () => { const d = await post('/api/tenant-enum', { target }); setTenantResults(d.data); }]);
    steps.push(['Buckets', async () => { const d = await post('/api/cloud-buckets', { target }); setCloudBucketsResults(d.data); }]);
    steps.push(['Third-party', async () => { const d = await post('/api/third-party', { target }); setThirdPartyResults(d.data); }]);
    steps.push(['Typosquat', async () => { const d = await post('/api/typosquat', { target }); setTyposquatResults(d.data); }]);
    if (mainIP) {
      steps.push(['PTR /24', async () => { const d = await post('/api/ptr-subnet', { target: mainIP }); setPtrResults(d.data); }]);
    }
    // stealth nmap отключён
    steps.push(['CVE auto', async () => {
      await refreshAutoCves('FULL');
    }]);


    setFullScanTimeline(steps.map(([name]) => ({ name, status: 'pending' })));
    for (let i = 0; i < steps.length; i++) {
      const [name, fn] = steps[i];
      setFullScanTimeline(prev => prev.map((s, j) => (j === i ? { ...s, status: 'running' } : s)));
      setSiteLog(prev => [...prev, `[FULL] → ${name}...`]);
      try {
        await fn();
        setFullScanTimeline(prev => prev.map((s, j) => (j === i ? { ...s, status: 'done' } : s)));
        setSiteLog(prev => [...prev, `[FULL] ✓ ${name}`]);
      } catch (e) {
        setFullScanTimeline(prev => prev.map((s, j) => (j === i ? { ...s, status: 'error' } : s)));
        setSiteLog(prev => [...prev, `[FULL] ✗ ${name}: ${e.message}`]);
      }
    }

    setFullScanning(false);
    setFullScanDone(true);
    setSiteActiveTab('overview');
    setSiteLog(prev => [...prev, `[FULL] ${t('Готово','Done')}`]);
    scanSnapRef.current = { ...(typeof buildExtraModules === 'function' ? buildExtraModules() : {}), ...scanSnapRef.current };
    setTimeout(() => {
      if (typeof refreshRiskIndex === 'function') refreshRiskIndex({ fullScan: true });
    }, 1200);
  };


  const buildExtraModules = () => ({
        shodan: shodanData,
        dirs: dirResults,
        dns: dnsResults,
        ssl: sslResults,
        js: jsResults,
        geo: geoResults,
        vhost: vhostResults,
        robots: robotsResults,
        admin: adminResults,
        cloud: cloudResults,
        security_headers: secHeadersResults,
        emails: emailResults,
        git: gitResults,
        whatweb: whatwebResults,
        cve: cveResults,
        stealth_scan: stealthScanResults,
        live_probe: liveProbeResults,
        email_dns: emailDnsResults,
        cors: corsResults,
        related: relatedResults,
        asn: asnResults,
        waf: wafResults,
        github: githubResults,
        ptr: ptrResults,
        passive_dns: passiveDnsResults,
        mx: mxResults,
        ipv6: ipv6Results,
        graphql: graphqlResults,
        sitemap_params: sitemapParamsResults,
        version_comments: versionCommentsResults,
        mobile_api: mobileApiResults,
        websocket: websocketResults,
        tenant: tenantResults,
        cloud_buckets: cloudBucketsResults,
        third_party: thirdPartyResults,
        typosquat: typosquatResults,
        ct: ctResults,
        nuclei: nucleiResults,
        wayback: waybackResults,
        dnssec: dnssecResults,
        asn_neighbors: asnNeighborsResults,
      });


  /* RISK_INDEX_UI */
  const refreshRiskIndex = async (opts = {}) => {
    if (!reportData) { setRiskIndex(null); return; }
    const full = opts.fullScan != null ? opts.fullScan : fullScanDone;
    try {
      const res = await fetch('/api/risk-index', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken()}`,
        },
        body: JSON.stringify({
          target: reportData.target,
          report_data: reportData,
          extra_modules: (typeof buildReportExtras === 'function' ? buildReportExtras() : { ...(typeof buildExtraModules === 'function' ? buildExtraModules() : {}), ...(scanSnapRef.current || {}) }),
          full_scan: full,
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setRiskIndex(data);
    } catch (e) { /* ignore */ }
  };

  const renderRiskGauge = (compact = false) => {
    if (!riskIndex || riskIndex.score == null) return null;
    const sc = Number(riskIndex.score);
    const col = riskIndex.color || (sc >= 7 ? '#ff6644' : sc >= 4 ? '#ffcc44' : '#44cc88');
    const pct = Math.min(100, Math.max(0, sc * 10));
    if (compact) {
      return (
        <span style={{
          padding: '4px 10px', borderRadius: 999, border: `1px solid ${col}`,
          color: col, fontWeight: 800, fontSize: 12, background: `${col}18`,
        }} title={riskIndex.label_ru || ''}>
          ⚠ {riskIndex.display || `${sc}/10`}
        </span>
      );
    }
    return (
      <div style={{
        marginBottom: 14, padding: '16px 18px', borderRadius: 14,
        border: `1px solid ${col}88`, background: `linear-gradient(135deg, ${col}14, rgba(0,8,6,0.85))`,
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
          <span style={{ fontSize: 11, letterSpacing: 2, opacity: 0.7, marginRight: 10 }}>ИНДЕКС УЯЗВИМОСТИ</span>
          <span style={{ fontSize: 32, fontWeight: 900, color: col, lineHeight: 1, marginRight: 12 }}>{riskIndex.display || `${sc}/10`}</span>
          <span style={{ fontSize: 13, opacity: 0.85, flex: '1 1 200px' }}>{riskIndex.label_ru || ''}</span>
        </div>
        <div style={{ height: 10, borderRadius: 6, background: 'rgba(0,0,0,0.35)', overflow: 'hidden', marginBottom: 8 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: col, borderRadius: 6, transition: 'width 0.4s ease' }} />
        </div>
        <div style={{ fontSize: 11, opacity: 0.65, lineHeight: 1.5 }}>
          {riskIndex.disclaimer_ru || '0 = жёсткая поверхность, 10 = максимум автоматических сигналов для атаки.'}
          {' '}· достоверность: {riskIndex.confidence_ru || riskIndex.confidence || '—'}
        </div>
        {(riskIndex.factors || []).slice(0, 4).map((f, i) => (
          <div key={i} style={{ fontSize: 11, color: '#a8e8c8', marginTop: 4 }}>
            +{f.points} — {f.name}: {f.detail}
          </div>
        ))}
      </div>
    );
  };


  /* RISK_SYNC_PDF */
  const buildReportExtras = () => {
    const snap = scanSnapRef.current || {};
    const extra_modules = { ...(typeof buildExtraModules === 'function' ? buildExtraModules() : {}), ...snap };
    const pdfCves = [...(cveResults?.cves || [])];
    const pdfSeen = new Set(pdfCves.map(c => c.id).filter(Boolean));
    const pullShodan = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      if (obj.vulns) {
        const vs = Array.isArray(obj.vulns) ? obj.vulns : Object.keys(obj.vulns);
        vs.forEach(v => {
          const id = typeof v === 'string' ? v : (v?.id || v?.cve);
          if (id && String(id).startsWith('CVE') && !pdfSeen.has(id)) {
            pdfSeen.add(id);
            pdfCves.push(typeof v === 'object' ? { ...v, id: String(id), source: 'shodan' } : { id: String(id), title: String(id), source: 'shodan' });
          }
        });
      }
      Object.values(obj).forEach(pullShodan);
    };
    const sh = shodanData || snap.shodan;
    if (sh) pullShodan(sh);
    extra_modules.cve = { ...(extra_modules.cve || {}), cves: pdfCves, source: 'report_merged' };
    return extra_modules;
  };

  const downloadPdfReport = async () => {
    if (!reportData) return;
    setPdfLoading(true);
    try {
      const extra_modules = buildReportExtras();
      console.log('PDF extra_modules keys', Object.keys(extra_modules).filter(k => extra_modules[k]));

      const res = await fetch('/api/generate-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          target: reportData.target,
          report_data: reportData,
          extra_modules,
        }),
      });

      if (!res.ok) throw new Error('Ошибка генерации PDF');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DarkAI_${reportData.target}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      if (typeof refreshRiskIndex === 'function') refreshRiskIndex({ fullScan: true });
      setSiteLog(prev => [...prev, `[PDF] Русский отчёт (уязвимости): DarkAI_${reportData.target}.pdf`]);
    } catch (err) {
      setSiteLog(prev => [...prev, `[PDF] Ошибка: ${err.message}`]);
    }
    setPdfLoading(false);
  };
  const t = (ru, en) => (lang === 'ru' ? ru : en);
  const toggleLang = () => {
    const n = lang === 'ru' ? 'en' : 'ru';
    setLang(n);
    localStorage.setItem('darkai_lang', n);
  };
  const cvssLevel = (score) => {
    const s = parseFloat(score);
    if (isNaN(s)) return 'unknown';
    if (s >= 9) return 'critical';
    if (s >= 7) return 'high';
    if (s >= 4) return 'medium';
    return 'low';
  };
  const cvssColor = (score) => {
    const l = cvssLevel(score);
    return ({ critical: '#ff2244', high: '#ff6600', medium: '#ffcc00', low: '#00ff88', unknown: '#00ff88' })[l];
  };
  const runCorsRedirect = async () => {
    if (!reportData) return;
    try {
      const res = await fetch('/api/cors-redirect', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setCorsResults(data.data);
      setSiteActiveTab('cors');
    } catch (e) { setSiteLog(prev => [...prev, `[CORS] ${e.message}`]); }
  };
  const runEmailDns = async () => {
    if (!reportData) return;
    try {
      const res = await fetch('/api/email-dns', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setEmailDnsResults(data.data);
      setSiteActiveTab('emaildns');
    } catch (e) { setSiteLog(prev => [...prev, `[EmailDNS] ${e.message}`]); }
  };
  const runAsn = async (ip) => {
    const target = ip || reportData?.target;
    if (!target) return;
    try {
      const res = await fetch('/api/asn-lookup', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target }) });
      const data = await res.json();
      setAsnResults(data.data);
      setSiteActiveTab('asn');
    } catch (e) { setSiteLog(prev => [...prev, `[ASN] ${e.message}`]); }
  };
  const runRelated = async () => {
    if (!reportData) return;
    try {
      const res = await fetch('/api/related-domains', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ target: reportData.target }) });
      const data = await res.json();
      setRelatedResults(data.data);
      setSiteActiveTab('related');
    } catch (e) { setSiteLog(prev => [...prev, `[Related] ${e.message}`]); }
  };
  const runLiveProbe = async () => {
    if (!reportData) return;
    const subs = reportData?.raw_data?.subdomains || [reportData.target];
    setSiteLog(prev => [...prev, `[Live] Probe ${subs.length} hosts...`]);
    try {
      const res = await fetch('/api/live-probe', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' }, body: JSON.stringify({ subdomains: subs }) });
      const data = await res.json();
      setLiveProbeResults(data);
      setSiteActiveTab('subdomains');
      setSiteLog(prev => [...prev, `[Live] live=${data.live?.length || 0} / ${data.probed || 0}`]);
    } catch (e) { setSiteLog(prev => [...prev, `[Live] ${e.message}`]); }
  };
  
  const renderGraphMainPage = () => {
    const card = (label, val, col = '#00ff88', hint = '') => (
      <div key={label} title={hint} style={{
        flex: '1 1 150px', minWidth: 130, padding: '16px 14px', borderRadius: 12,
        border: `1px solid ${col}55`, background: `linear-gradient(145deg, ${col}12, rgba(0,12,10,0.85))`,
        textAlign: 'center', boxShadow: `0 4px 24px ${col}18`
      }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: col, lineHeight: 1.1 }}>{val}</div>
        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 6, letterSpacing: 0.6, textTransform: 'uppercase' }}>{label}</div>
      </div>
    );
    const legend = (
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18, padding: '12px 14px',
        borderRadius: 10, border: '1px solid rgba(0,255,136,0.2)', background: 'rgba(0,8,6,0.6)', fontSize: 12,
        alignItems: 'center',
      }}>
        {[
          ['domain', '#00ccff', 'домен'],
          ['hosts', '#00ccff', 'хосты'],
          ['net', '#66ffaa', 'сеть'],
          ['tech', '#aaccff', 'стек'],
          ['web', '#ffcc66', 'web'],
          ['cve', '#ff6688', 'CVE'],
        ].map(([key, col, lab]) => (
          <button key={key} type="button" onClick={() => setGraphHighlightLane(graphHighlightLane === key ? null : key)}
            style={{
              background: graphHighlightLane === key ? `${col}33` : 'transparent',
              border: graphHighlightLane === key ? `1px solid ${col}` : '1px solid transparent',
              borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: col,
            }}>
            <b style={{ color: col }}>●</b> {lab}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.55 }}>клик — подсветка колонки</span>
      </div>
    );
    if (!reportData) {
      return (
        <GuidePanel style={{ maxWidth: 720, margin: '24px auto', textAlign: 'left' }}>
          <h2 style={{ marginBottom: 8 }}>📊 Граф связей</h2>
          <p style={{ opacity: 0.9, lineHeight: 1.55 }}>
            Интерактивная схема: <b>домен → поддомены → IP → порты → CVE</b>.
          </p>
          <ol style={{ lineHeight: 1.7, opacity: 0.92, paddingLeft: 20 }}>
            <li>Меню слева: <b>🔍 Сканирование</b> — введите домен и нажмите <b>ОТПРАВИТЬ</b>.</li>
            <li>Дождитесь обзора или запустите <b>FULL SCAN</b> для CVE и Shodan.</li>
            <li>Вернитесь сюда — граф построится по данным скана.</li>
          </ol>
          <p style={{ marginTop: 16, fontSize: 12, opacity: 0.65 }}>Сетевой nmap отключён — меньше обрывов и таймаутов.</p>
        </GuidePanel>
      );
    }
    const raw = reportData.raw_data || {};
    const subs = raw.subdomains || [];
    const ips = Object.values(raw.subdomain_ips || {}).flat().filter(Boolean);
    const cveN = (cveResults?.cves || []).length;
    const liveN = liveProbeResults?.live?.length || 0;
    return (
      <GuidePanel style={{ maxWidth: '100%', width: '100%' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>📊 Граф — {reportData.target}</h2>
          <span style={{ fontSize: 12, opacity: 0.7 }}>прокрутка по горизонтали при большом числе узлов</span>
        </div>
        {legend}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
          {card('Поддомены', subs.length, '#00ccff', 'из passive DNS / перечисления')}
          {card('Уник. IP', [...new Set(ips)].length, '#66ffaa')}
          {card('Live HTTP', liveN, '#00ff88', 'ответили на проверку')}
          {card('CVE', getMergedCveList(cveResults?.cves || []).length, '#ff6688', 'API + Shodan как на глобусе')}
        </div>
        <div style={{
          padding: 18, borderRadius: 14,
          border: '1px solid rgba(0,255,136,0.28)',
          background: 'linear-gradient(180deg, rgba(0,14,11,0.75), rgba(0,6,5,0.92))',
          overflowX: 'auto', minHeight: 280
        }}>
          {renderRelationGraph()}
        </div>
      </GuidePanel>
    );
  };

  const renderRelationGraph = () => {
    if (!reportData) return <div style={{ opacity: 0.6 }}>{t('Сначала выполните разведку','Run recon first')}</div>;
    const domain = reportData.target;
    const raw = reportData.raw_data || {};
    const subs = (raw.subdomains || []).slice(0, 12);
    const live = (liveProbeResults?.live || []).slice(0, 8);
    const ipsMap = raw.subdomain_ips || {};
    const allIps = [...new Set(Object.values(ipsMap).flat().filter(Boolean))].slice(0, 8);
    const shPorts = (shodanData?.ports || shodanData?.structured?.ports || []);
    const ports = (stealthScanResults?.open_ports || shPorts || []).slice(0, 10).map(p => (typeof p === 'object' ? p : { port: p }));
    const cves = getMergedCveList(cveResults?.cves || []).slice(0, 24);
    const techs = (whatwebResults?.technologies || whatwebResults?.plugins || whatwebResults || []);
    const techList = (Array.isArray(techs) ? techs : Object.keys(techs || {})).slice(0, 8).map(t => (typeof t === 'string' ? t : (t.name || t.plugin || String(t))));
    const emails = (emailResults?.emails || emailResults || []);
    const emailN = Array.isArray(emails) ? emails.length : (emails && typeof emails === 'object' ? Object.keys(emails).length : 0);
    const dirsN = Array.isArray(dirResults) ? dirResults.length : (dirResults?.found?.length || 0);
    const nucleiN = (nucleiResults?.matches || nucleiResults?.findings || nucleiResults || []);
    const nucleiCount = Array.isArray(nucleiN) ? nucleiN.length : (nucleiResults?.count || 0);
    const mxN = (mxResults?.records || mxResults || []);
    const mxCount = Array.isArray(mxN) ? mxN.length : 0;

    const lanes = [
      { key: 'domain', title: '🌐 Домен', color: '#00ff88', items: [{ id: 'dom', label: domain }] },
      { key: 'hosts', title: '🔗 Хосты', color: '#00ccff', items: [
        ...subs.map((s, i) => ({ id: 's' + i, label: s })),
        ...live.map((u, i) => ({ id: 'l' + i, label: u, sub: 'live' })),
      ].slice(0, 14) },
      { key: 'net', title: '📡 Сеть', color: '#66ffaa', items: [
        ...allIps.map((ip, i) => ({ id: 'ip' + i, label: ip })),
        ...(sslResults && !sslResults.error ? [{ id: 'tls', label: 'TLS ' + (sslResults.days_left ?? '?') + 'd' }] : []),
        ...(sslResults && !sslResults.error ? [{ id: 'tls', label: 'TLS ' + (sslResults.days_left ?? '?') + 'd' }] : []),
        ...(sslResults && !sslResults.error ? [{ id: 'tls', label: 'TLS ' + (sslResults.days_left ?? '?') + 'd' }] : []),
        ...(sslResults && !sslResults.error ? [{ id: 'tls', label: 'TLS ' + (sslResults.days_left ?? '?') + 'd' }] : []),
        ...ports.map((p, i) => ({ id: 'p' + i, label: 'p:' + (p.port || p) })),
        ...(geoResults?.country ? [{ id: 'geo', label: geoResults.country }] : []),
        ...(asnResults?.asn ? [{ id: 'asn', label: 'AS' + asnResults.asn }] : []),
      ].slice(0, 14) },
      { key: 'tech', title: '🧩 Стек', color: '#aaccff', items: techList.map((x, i) => ({ id: 't' + i, label: x })) },
      { key: 'web', title: '🌍 Web / данные', color: '#ffcc66', items: [
        ...(dirsN ? [{ id: 'dirs', label: 'dirs:' + dirsN }] : []),
        ...(nucleiCount ? [{ id: 'nuc', label: 'nuclei:' + nucleiCount }] : []),
        ...(emailN ? [{ id: 'em', label: 'email:' + emailN }] : []),
        ...(mxCount ? [{ id: 'mx', label: 'MX:' + mxCount }] : []),
        ...(waybackResults?.urls?.length ? [{ id: 'wb', label: 'wayback:' + waybackResults.urls.length }] : []),
        ...(ctResults?.subdomains?.length ? [{ id: 'ct', label: 'CT:' + ctResults.subdomains.length }] : []),
      ] },
      { key: 'cve', title: '🛡 CVE', color: '#ff6688', items: cves.map((c, i) => ({ id: 'c' + i, label: c.id, cve: c })) },
    ];

    const W = 1180, colW = W / lanes.length, rowH = 36, headerH = 44;
    const maxRows = Math.max(...lanes.map(l => Math.max(l.items.length, 1)), 1);
    const H = headerH + maxRows * rowH + 48;

    const nodePos = {};
    lanes.forEach((lane, li) => {
      const cx = colW * li + colW / 2;
      lane.items.forEach((it, ri) => {
        nodePos[it.id] = { x: cx, y: headerH + 18 + ri * rowH, lane: lane.key, ...it, color: lane.color };
      });
      if (!lane.items.length) {
        nodePos[lane.key + '_empty'] = { x: cx, y: headerH + 18, lane: lane.key, label: '—', color: lane.color, empty: true };
      }
    });

    const domId = 'dom';
    const edges = [];
    lanes.slice(1).forEach(lane => {
      lane.items.forEach(it => edges.push([domId, it.id]));
      if (!lane.items.length) edges.push([domId, lane.key + '_empty']);
    });

    const chip = (node) => {
      const lab = String(node.label || '—');
      const short = lab.length > 18 ? lab.slice(0, 16) + '…' : lab;
      const stroke = node.cve ? cvssColor(node.cve.cvss) : node.color;
      const dim = graphHighlightLane && node.lane !== graphHighlightLane;
      return (
        <g key={node.id} onClick={() => node.cve && setSelectedCve(node.cve)} style={{
          cursor: node.cve ? 'pointer' : 'default',
          opacity: dim ? 0.28 : 1,
          animation: 'graphNodeIn 0.45s ease',
        }}>
          <rect x={node.x - 58} y={node.y - 14} width={116} height={28} rx={8}
            fill="rgba(0,14,11,0.92)" stroke={stroke} strokeWidth={graphHighlightLane === node.lane ? 2.5 : (node.empty ? 1 : 1.5)} />
          <text x={node.x} y={node.y + 4} textAnchor="middle" fill={stroke} fontSize="10" fontFamily="monospace">{short}</text>
        </g>
      );
    };

    return (
      <div style={{ overflowX: 'auto' }}>
        <p style={{ color: '#00ccaa', fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
          Полная карта находок: домен → хосты → сеть → технологии → web-модули → CVE (клик по CVE — описание).
        </p>
        <svg width={W} height={H} style={{
          background: 'linear-gradient(180deg, rgba(0,16,12,0.85), rgba(0,6,5,0.95))',
          borderRadius: 12, border: '1px solid rgba(0,255,136,0.25)', minWidth: W
        }}>
          {lanes.map((lane, li) => (
            <g key={lane.key}>
              <rect x={colW * li + 4} y={8} width={colW - 8} height={headerH - 12} rx={8}
                fill="rgba(0,255,136,0.06)" stroke="rgba(0,255,136,0.15)" />
              <text x={colW * li + colW / 2} y={28} textAnchor="middle" fill={lane.color} fontSize="11" fontWeight="bold">{lane.title}</text>
            </g>
          ))}
          {edges.map(([a, b], i) => {
            const pa = nodePos[a], pb = nodePos[b];
            if (!pa || !pb) return null;
            return <line key={'e' + i} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke="rgba(0,255,136,0.12)" strokeWidth="1" strokeDasharray="4 3" />;
          })}
          {Object.values(nodePos).map(chip)}
        </svg>
      </div>
    );
  };

  const runLeakCheck = async () => {
    const q = (leakEmail || '').trim();
    if (!q) return;
    setLeakLoading(true);
    try {
      const res = await fetch('/api/leakcheck', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ query: q, type: 'email' }),
      });
      const data = await res.json();
      setLeakResults(data.leakcheck || data);
    } catch (e) {
      setLeakResults({ error: e.message, found: 0, result: [] });
    }
    setLeakLoading(false);
  };
  const runWorkplaceLocation = async (domainOverride) => {
    const domain = (domainOverride || workplaceDomain || reportData?.target || '').trim();
    if (!domain) return;
    setWorkplaceDomain(domain);
    setWorkplaceLoading(true);
    try {
      const res = await fetch('/api/workplace-location', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' },
        body: JSON.stringify({ target: domain, whois: reportData?.raw_data?.whois || '' }),
      });
      const data = await res.json();
      setWorkplaceResults(data.workplace || data);
    } catch (e) {
      setWorkplaceResults({ error: e.message });
    }
    setWorkplaceLoading(false);
  };

  const renderWorkplace = () => {
    const loc = workplaceResults?.location;
    const f4 = loc ? `https://demo.f4map.com/#lat=${loc.lat}&lon=${loc.lon}&zoom=18&camera.theta=0.55` : null;
    return (
      <GuidePanel>
        <h2>📍 Рабочее местоположение домена</h2>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <input value={workplaceDomain} onChange={(e) => setWorkplaceDomain(e.target.value)} placeholder="domain.com"
            style={{ flex: 1, minWidth: 200, background: '#0a0c10', color: '#00ff88', border: '1px solid rgba(0,255,136,0.4)', padding: '10px 12px', borderRadius: 6, fontFamily: 'inherit', fontSize: 14 }} />
          <Button onClick={() => runWorkplaceLocation()} disabled={workplaceLoading} style={{ marginTop: 0 }}>
            {workplaceLoading ? 'Поиск...' : 'Определить адрес'}
          </Button>
        </div>
        {workplaceResults?.error && <div style={{ color: '#ff5555' }}>{String(workplaceResults.error)}</div>}
        {workplaceResults && !workplaceResults.error && (
          <div style={{ color: '#00ff88', fontSize: 14, lineHeight: 1.6, marginBottom: 12 }}>
            <div><b>Адрес:</b> {workplaceResults.address || loc?.display_name || '—'}</div>
            {loc && <div><b>Координаты:</b> {Number(loc.lat).toFixed(6)}, {Number(loc.lon).toFixed(6)}</div>}
          </div>
        )}
        {f4 && (
          <iframe title="workplace-3d" src={f4} style={{ width: '100%', height: '60vh', border: '1px solid rgba(0,255,136,0.3)', borderRadius: 8 }} allowFullScreen />
        )}
      </GuidePanel>
    );
  };

  const renderLeaks = () => (
    <GuidePanel>
      <h2>🔓 Утечки по паролям</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={leakEmail}
          onChange={(e) => setLeakEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runLeakCheck(); }}
          placeholder="email@example.com"
          style={{
            flex: 1, minWidth: 220, background: '#0a0c10', color: '#00ff88',
            border: '1px solid rgba(0,255,136,0.4)', padding: '10px 12px',
            borderRadius: 6, fontFamily: 'inherit', fontSize: 14
          }}
        />
        <Button onClick={runLeakCheck} disabled={leakLoading} style={{ marginTop: 0 }}>
          {leakLoading ? 'Проверка...' : 'Проверить'}
        </Button>
      </div>
      {leakResults?.error && (
        <div style={{ color: '#ff5555', marginBottom: 12 }}>{String(leakResults.error)}</div>
      )}
      {leakResults && !leakResults.error && (
        <div style={{ color: '#00ff88', marginBottom: 12 }}>
          <b>Найдено:</b> {leakResults.found ?? 0}
          {leakResults.quota != null && <span style={{ opacity: 0.6 }}> · quota: {leakResults.quota}</span>}
        </div>
      )}
      {leakResults?.result?.length > 0 && (
        <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {leakResults.result.map((row, i) => (
            <div key={i} style={{
              border: '1px solid rgba(0,255,136,0.2)', borderLeft: '3px solid #00ff88',
              padding: '10px 12px', marginBottom: 8, borderRadius: '0 6px 6px 0',
              background: 'rgba(0,15,12,0.45)', fontSize: 13, lineHeight: 1.55
            }}>
              <div><b>Источник:</b> {row.source || '—'} {row.breach_date ? `(${row.breach_date})` : ''}</div>
              {row.email && <div><b>Email:</b> {row.email}</div>}
              {row.username && <div><b>Username:</b> {row.username}</div>}
              {row.password && <div style={{ color: '#ffcc00' }}><b>Password:</b> {row.password}</div>}
              {(row.first_name || row.last_name) && (
                <div><b>Имя:</b> {[row.first_name, row.last_name].filter(Boolean).join(' ')}</div>
              )}
              {row.phone && <div><b>Телефон:</b> {row.phone}</div>}
              {row.fields?.length > 0 && (
                <div style={{ opacity: 0.7 }}><b>Поля:</b> {row.fields.join(', ')}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {leakResults && !leakResults.error && (leakResults.found === 0 || leakResults.result?.length === 0) && (
        <div style={{ opacity: 0.7 }}>Утечек не найдено</div>
      )}
    </GuidePanel>
  );
  const saveCurrentScanToHistory = async () => {
    if (!reportData) return;
    try {
      const res = await fetch('/api/scan-history/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' },
        body: JSON.stringify({
          target: reportData.target,
          report_data: reportData,
          extra_modules: {
            stealth_scan: stealthScanResults || undefined,
            cve: cveResults || undefined,
            live_probe: liveProbeResults || undefined,
            security_headers: secHeadersResults || undefined,
            shodan: shodanData || undefined,
          },
        }),
      });
      const data = await res.json();
      setSiteLog(prev => [...prev, `[History] Сохранено #${data.saved?.id || '?'}`]);
    } catch (e) {
      setSiteLog(prev => [...prev, `[History] Ошибка: ${e.message}`]);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch('/api/scan-history?limit=50', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      setHistoryScans(data.scans || []);
    } catch (e) {
      setHistoryScans([]);
    }
    setHistoryLoading(false);
  };

  const openScanFromHistory = async (id) => {
    try {
      const res = await fetch(`/api/scan-history/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      const scan = data.scan;
      if (!scan) return;
      setReportData(scan.report_data || null);
      const ex = scan.extra_modules || {};
      if (ex.cve) setCveResults(ex.cve);
      if (ex.shodan) setShodanData(ex.shodan);
      setMainTab('scan');
      setSiteActiveTab('overview');
      setSiteLog(prev => [...prev, `[History] Загружен скан #${id} — ${scan.target}`]);
    } catch (e) {
      setSiteLog(prev => [...prev, `[History] Ошибка загрузки: ${e.message}`]);
    }
  };

  const runCompare = async () => {
    if (!compareA || !compareB) return;
    setCompareLoading(true);
    try {
      const res = await fetch('/api/scan-history/compare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' },
        body: JSON.stringify({ id_a: Number(compareA), id_b: Number(compareB) }),
      });
      const data = await res.json();
      setCompareResult(data);
    } catch (e) {
      setCompareResult({ error: e.message });
    }
    setCompareLoading(false);
  };

  const renderHistory = () => (
    <GuidePanel>
      <h2>📜 История сканов</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Button onClick={loadHistory} disabled={historyLoading} style={{ marginTop: 0 }}>
          {historyLoading ? 'Загрузка...' : 'Обновить список'}
        </Button>
        <Button onClick={saveCurrentScanToHistory} disabled={!reportData} style={{ marginTop: 0 }}>
          Сохранить текущий скан
        </Button>
      </div>
      {(historyScans || []).length === 0 && (
        <div style={{ opacity: 0.6 }}>Пока пусто. После FULL SCAN нажми «Сохранить текущий скан».</div>
      )}
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {(historyScans || []).map((s) => (
          <div key={s.id} style={{
            border: '1px solid rgba(0,255,136,0.25)', borderRadius: 8,
            padding: '12px 14px', marginBottom: 10, background: 'rgba(0,12,10,0.4)',
            display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center'
          }}>
            <div style={{ color: '#00ff88', lineHeight: 1.5, fontSize: 14 }}>
              <div><b>#{s.id}</b> · {s.target}</div>
              <div style={{ opacity: 0.75, fontSize: 12 }}>{s.created_at} UTC · {s.status} · {s.user || '—'}</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                sub: {s.summary?.subdomains ?? 0} · ports: {s.summary?.ports ?? 0} · cve: {s.summary?.cves ?? 0} · live: {s.summary?.live ?? 0}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={() => openScanFromHistory(s.id)} style={{ marginTop: 0 }}>Открыть</Button>
              <Button onClick={() => { setCompareA(String(s.id)); setMainTab('compare'); }} style={{ marginTop: 0 }}>В сравнение A</Button>
              <Button onClick={() => { setCompareB(String(s.id)); setMainTab('compare'); }} style={{ marginTop: 0 }}>В сравнение B</Button>
            </div>
          </div>
        ))}
      </div>
    </GuidePanel>
  );

  const renderCompare = () => (
    <GuidePanel>
      <h2>🔀 Сравнение сканов</h2>
      <p style={{ opacity: 0.7, marginBottom: 12 }}>Укажи ID двух сохранений (из Истории) — покажем разницу по поддоменам, портам и заголовкам.</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={compareA} onChange={(e) => setCompareA(e.target.value)} placeholder="ID A"
          style={{ width: 100, background: '#0a0c10', color: '#00ff88', border: '1px solid rgba(0,255,136,0.4)', padding: 8, borderRadius: 6 }} />
        <span style={{ color: '#00ff88' }}>vs</span>
        <input value={compareB} onChange={(e) => setCompareB(e.target.value)} placeholder="ID B"
          style={{ width: 100, background: '#0a0c10', color: '#00ff88', border: '1px solid rgba(0,255,136,0.4)', padding: 8, borderRadius: 6 }} />
        <Button onClick={runCompare} disabled={compareLoading || !compareA || !compareB} style={{ marginTop: 0 }}>
          {compareLoading ? '...' : 'Сравнить'}
        </Button>
        <Button onClick={() => { setMainTab('history'); loadHistory(); }} style={{ marginTop: 0 }}>К истории</Button>
      </div>
      {compareResult?.error && <div style={{ color: '#ff5555' }}>{compareResult.error}</div>}
      {compareResult && !compareResult.error && (
        <div style={{ color: '#00ff88', fontSize: 14, lineHeight: 1.6 }}>
          <div style={{ marginBottom: 12, opacity: 0.85 }}>
            A: #{compareResult.scan_a?.id} {compareResult.scan_a?.target} ({compareResult.scan_a?.created_at})<br/>
            B: #{compareResult.scan_b?.id} {compareResult.scan_b?.target} ({compareResult.scan_b?.created_at})
          </div>
          {['subdomains', 'ports', 'headers'].map((key) => (
            <div key={key} style={{
              border: '1px solid rgba(0,255,136,0.2)', borderRadius: 8, padding: 12, marginBottom: 12,
              background: 'rgba(0,12,10,0.35)'
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>{key}</div>
              <div><b style={{ color: '#66ffaa' }}>+ добавлено в B:</b> {(compareResult[key]?.added || []).join(', ') || '—'}</div>
              <div><b style={{ color: '#ff8866' }}>− убрано (было в A):</b> {(compareResult[key]?.removed || []).join(', ') || '—'}</div>
              <div style={{ opacity: 0.6 }}>без изменений: {compareResult[key]?.same_count ?? 0}</div>
            </div>
          ))}
        </div>
      )}
    </GuidePanel>
  );
  const fetchMe = async (tok) => {
    const tkn = (tok || authToken()).trim();
    if (!tkn) return;
    try {
      const res = await fetch('/api/me', { headers: { Authorization: `Bearer ${tkn}` } });
      if (!res.ok) {
        clearSession(t('Сессия истекла — войдите снова (логин + 2FA)', 'Session expired — log in again'));
        return;
      }
      const data = await res.json();
      setCurrentUser(data.user || '');
      setUserRole(data.role || 'analyst');
    } catch (e) {}
  };

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await fetch('/api/audit-log?limit=100', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.status === 403) { setAuditLogs([]); setAuditLoading(false); return; }
      const data = await res.json();
      setAuditLogs(data.logs || []);
    } catch (e) { setAuditLogs([]); }
    setAuditLoading(false);
  };

  const runCtLogs = async () => {
    if (!reportData?.target) return;
    setCtLoading(true);
    try {
      const res = await fetch('/api/ct-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-API-KEY': 'kDfN3j' },
        body: JSON.stringify({ target: reportData.target }),
      });
      const data = await res.json();
      setCtResults(data.ct || data);
      setSiteActiveTab('ct');
      const subs = (data.ct || data).subdomains || [];
      if (subs.length) {
        setReportData(prev => {
          if (!prev) return prev;
          const existing = new Set(prev.raw_data?.subdomains || []);
          subs.forEach(s => existing.add(s));
          return {
            ...prev,
            raw_data: { ...(prev.raw_data || {}), subdomains: Array.from(existing).sort() },
          };
        });
      }
    } catch (e) {
      setSiteLog(prev => [...prev, `[CT] ${e.message}`]);
    }
    setCtLoading(false);
  };

  const renderAudit = () => (
    <GuidePanel>
      <h2>📋 Аудит-лог</h2>
      <p style={{ opacity: 0.7 }}>Только admin (darkaiadmin).</p>
      <Button onClick={loadAudit} disabled={auditLoading} style={{ marginTop: 0, marginBottom: 12 }}>
        {auditLoading ? '...' : 'Обновить'}
      </Button>
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {(auditLogs || []).map(l => (
          <div key={l.id} style={{
            border: '1px solid rgba(0,255,136,0.2)', borderRadius: 6, padding: '8px 12px', marginBottom: 6,
            color: '#00ff88', fontSize: 13
          }}>
            <b>#{l.id}</b> {l.created_at} · <b>{l.user}</b> · {l.action} · {l.target || '—'}
            {l.detail ? <span style={{ opacity: 0.6 }}> · {l.detail}</span> : null}
          </div>
        ))}
      </div>
    </GuidePanel>
  )

  useEffect(() => { if (token) fetchMe(token); }, [token]);

  const renderGuide = () => (
    <GuidePanel>
      <h2>Руководство по использованию — ДАРК-РАЗВЕДКА</h2>
      <p>
        Система разведки по доменам и IP-адресам. Ниже описано, как пользоваться каждым разделом.
      </p>

      <h3>1. Вход</h3>
      <div className="step">
        <p>Введите логин и пароль, затем код двухфакторной аутентификации (2FA / TOTP). Сессия сохраняется в браузере.</p>
      </div>

      <h3>2. Базовая разведка</h3>
      <div className="step">
        <p>Откройте вкладку <b>СКАНИРОВАНИЕ</b>. В поле внизу введите домен или IP и нажмите <b>ОТПРАВИТЬ</b>.</p>
        <p>Система соберёт: WHOIS, поддомены (несколько источников), IP-адреса, технологии, сбор поддоменов и технологий (без nmap).</p>
      </div>

      <h3>3. Полное сканирование</h3>
      <div className="step">
        <p>После базовой разведки на вкладке <b>Обзор</b> нажмите <b>ПОЛНОЕ СКАНИРОВАНИЕ</b>.</p>
        <p>По очереди запускаются модули: WhatWeb, DNS, SSL, JS, robots, директории, админки, облака, заголовки, email, git, vhost, CORS, SPF/DKIM, связанные домены, live-probe, Shodan/Geo/ASN, WAF, GitHub dorks, Passive DNS, MX, IPv6, GraphQL, Sitemap params, Versions, Mobile API, WebSocket, Tenant, Buckets, 3rd party, Typosquat, PTR /24 (по IP), скрытый скан портов, CVE.</p>
        <p>Кнопка <b>Отмена сканирования</b> прерывает полный скан и долгие сетевые проверки.</p>
      </div>

      <h3>4. Вкладки модулей</h3>
      <ul>
        <li><b>Обзор</b> — сводка по цели, полный скан, PDF, ASN, связанные домены</li>
        <li><b>WHOIS</b> — данные регистрации домена</li>
        <li><b>Поддомены</b> — список поддоменов, Live-probe (проверка «живых» хостов), сортировка: все / с IP / без IP / live</li>
        <li><b>Shodan</b> — данные по IP; найденные CVE попадают на глобус</li>
        <li><b>Технологии / WhatWeb</b> — определение CMS и стека</li>
        <li><b>Директории</b> — перебор распространённых путей</li>
        <li><b>DNS-аудит</b> — DNS-записи</li>
        <li><b>SSL/TLS</b> — сертификат и параметры TLS</li>
        <li><b>JS-анализ</b> — скрипты, эндпоинты, возможные ключи</li>
        <li><b>Геолокация</b> — GeoIP по адресу</li>
        <li><b>VHOST-брутфорс</b> — виртуальные хосты</li>
        <li><b>robots/sitemap</b> — служебные файлы сайта</li>
        <li><b>Админки</b> — поиск панелей администратора</li>
        <li><b>Облака</b> — проверка открытых облачных хранилищ</li>
        <li><b>Заголовки</b> — security headers</li>
        <li><b>Email</b> — сбор адресов с сайта</li>
        <li><b>Git/Бэкапы</b> — утечки .git и резервных копий</li>
        <li><b>CVE</b> — автоматически после полного скана (без ручного ввода)</li>
        
        <li><b>CORS/Redirect</b> — проверка CORS и open redirect</li>
        <li><b>SPF/DKIM</b> — почтовые DNS-политики (SPF, DKIM, DMARC)</li>
        <li><b>ASN</b> — автономная система и провайдер</li>
        <li><b>Related</b> — домены на том же IP, в том же сертификате, analytics ID</li>
        <li><b>WAF</b> — определение защиты (Cloudflare, Akamai и др.)</li>
        <li><b>GitHub</b> — поиск упоминаний домена и возможных утечек в репозиториях</li>
        <li><b>Граф</b> — схема связей: домен → поддомены → IP → порты → CVE</li>
        <li><b>PTR /24</b> — обратные PTR-имена соседних хостов в подсети /24 по IP цели</li>
        <li><b>Passive DNS</b> — история DNS (AlienVault OTX): старые A/NS и связанные имена</li>
        <li><b>MX</b> — почтовые MX, провайдер (Google/Microsoft и др.), замечания по SPF/DMARC</li>
        <li><b>IPv6</b> — поиск AAAA и IPv6 у домена и типичных поддоменов</li>
        <li><b>GraphQL</b> — поиск эндпоинтов GraphQL и открытой интроспекции</li>
        <li><b>Sitemap params</b> — параметры из sitemap.xml (?id=, ?page= и т.д.)</li>
        <li><b>Versions</b> — версии и generator в HTML/JS (jQuery, React, WordPress и др.)</li>
        <li><b>Mobile API</b> — проверка api./m./app. и типичных API-путей</li>
        <li><b>WebSocket</b> — поиск ws/wss и socket-эндпоинтов в коде страницы</li>
        <li><b>Tenant</b> — проверка организации в Microsoft 365 / Google Workspace</li>
        <li><b>Buckets</b> — возможные публичные S3 / Azure / GCS по имени организации</li>
        <li><b>3rd party</b> — внешние скрипты и трекеры (CDN, аналитика)</li>
        <li><b>Typosquat</b> — похожие домены (опечатки), которые резолвятся в DNS</li>
      </ul>

      <h3>5. Карта уязвимостей (глобус)</h3>
      <div className="step">
        <p>Справа отображается 3D-глобус с CVE. Цвет зависит от оценки CVSS. Можно фильтровать: все / критичные / высокие / средние / низкие. Нажмите на CVE, чтобы открыть описание.</p>
      </div>

      <h3>6. PDF-отчёт</h3>
      <div className="step">
        <p>Скачать PDF можно только после успешного <b>полного сканирования</b>. В отчёт входят все собранные разделы: WHOIS, поддомены, live-хосты, технологии, порты, Shodan, DNS/SSL, web-проверки, email, связанные домены, CVE, WAF, GitHub, Passive DNS, MX, IPv6, PTR, GraphQL, sitemap-параметры, versions, mobile API, WebSocket, tenant, buckets, third-party, typosquat и др.</p>
      </div>

      <h3>7. Рекомендации</h3>
      <ul>
        <li>Сначала всегда нажимайте «Отправить» по цели, затем — полное сканирование.</li>
        <li>При возникновении ошибки: [ERROR] Unexpected token '&lt;', "&lt;!DOCTYPE "... is not valid JSON — повторить вставку домена с нажатием кнопки «Отправить».</li>
        <li>Работать с ДАРК-РАЗВЕДКА строго с компьютера/ноутбука.</li>
        <li>CVE в Shodan есть не у каждого IP — это ограничение базы Shodan.</li>
      </ul>


    </GuidePanel>
  );

  const renderCveGlobe = () => {
    let cves = [...(cveResults?.cves || [])];
    const seen = new Set(cves.map(c => c.id).filter(Boolean));
    const extractShodanCves = (obj, acc = []) => {
      if (!obj || typeof obj !== 'object') return acc;
      if (Array.isArray(obj)) { obj.forEach(x => extractShodanCves(x, acc)); return acc; }
      if (obj.vulns) {
        if (Array.isArray(obj.vulns)) {
          obj.vulns.forEach(v => {
            const id = typeof v === 'string' ? v : v?.id || v?.cve;
            if (id && String(id).startsWith('CVE') && !seen.has(id)) {
              seen.add(id);
              acc.push({ id, title: id, description: typeof v === 'object' ? (v.summary || v.description || '') : 'Найдено в Shodan', cvss: v?.cvss || v?.cvss3 || null, source: 'shodan' });
            }
          });
        } else if (typeof obj.vulns === 'object') {
          Object.entries(obj.vulns).forEach(([id, v]) => {
            if (String(id).startsWith('CVE') && !seen.has(id)) {
              seen.add(id);
              acc.push({ id, title: id, description: v?.summary || v?.description || 'Найдено в Shodan', cvss: v?.cvss || v?.cvss3 || null, source: 'shodan' });
            }
          });
        }
      }
      Object.values(obj).forEach(v => { if (v && typeof v === 'object') extractShodanCves(v, acc); });
      return acc;
    };
    if (shodanData) cves = cves.concat(extractShodanCves(shodanData));
    const cvePoolSize = cves.length;
    cves = filterCvesBySource(cves);
    const sourceFilterEmpty = cvePoolSize > 0 && cves.length === 0;
    if (!reportData || cvePoolSize === 0) return null;
    // CVSS filter
    const filtered = cveCvssFilter === 'all' ? cves : cves.filter(c => cvssLevel(c.cvss) === cveCvssFilter);
    const viewCves = filtered.length ? filtered : cves;

    const domain = reportData.target;
    const cx = 50, cy = 50;
    const n = (typeof viewCves !== 'undefined' ? viewCves : cves).length;
    const drawList = typeof viewCves !== 'undefined' ? viewCves : cves;
    // Несколько орбит: чем больше CVE, тем больше колец
    const rings = Math.max(2, Math.min(5, Math.ceil(n / 14)));
    const posFor = (i) => {
      const ring = i % rings;
      const onRing = Math.floor(i / rings);
      const countOnRing = Math.ceil(n / rings);
      const radius = 28 + ring * ((48 - 28) / Math.max(rings - 1, 1));
      const angle = (2 * Math.PI * onRing) / countOnRing - Math.PI / 2 + ring * 0.25;
      return {
        left: cx + radius * Math.cos(angle),
        top: cy + radius * Math.sin(angle),
        radius,
        angle,
      };
    };

    return (
      <VizSection>
        <div style={{ color: '#00ccaa', fontSize: 18, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
          🌐 Карта уязвимостей CVE
        </div>
        <div style={{ color: '#00ff88', fontSize: 15, marginBottom: 10, opacity: 0.85 }}>
          {t('Цель','Target')}: <b>{domain}</b> · {n} CVE
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {[
            ['all', t('Все','All'), '#00ff88'],
            ['critical', '9.0+', '#ff2244'],
            ['high', '7.0+', '#ff6600'],
            ['medium', '4.0+', '#ffcc00'],
            ['low', '0–3.9', '#66ffaa'],
          ].map(([k, label, col]) => (
            <button key={k} onClick={() => setCveCvssFilter(k)} style={{
              background: cveCvssFilter === k ? col : 'transparent',
              color: cveCvssFilter === k ? '#000' : col,
              border: `1px solid ${col}`,
              borderRadius: 6, padding: '6px 12px', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer'
            }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {[
            ['all', 'Источник: все', '#00ff88'],
            ['shodan', 'Shodan', '#66ccff'],
            ['nuclei', 'Nuclei', '#ffaa44'],
            ['tech', 'Tech/NVD', '#cc88ff'],
          ].map(([k, label, col]) => (
            <button key={'src-' + k} type="button" onClick={() => setCveSourceFilter(k)} style={{
              background: cveSourceFilter === k ? col : 'transparent',
              color: cveSourceFilter === k ? '#000' : col,
              border: `1px solid ${col}`,
              borderRadius: 6, padding: '5px 10px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer'
            }}>{label}</button>
          ))}
        </div>
        {sourceFilterEmpty && (
          <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid #ffaa44', color: '#ffdd88', fontSize: 13 }}>
            Нет CVE для фильтра «{cveSourceFilter}» — нажмите «Источник: все» или другой источник. Всего в пуле: {cvePoolSize}.
          </div>
        )}
        <GlobeWrap>
          <CveGlobe3D
            domain={domain}
            cves={drawList}
            cvssColor={cvssColor}
            sourceColor={cveSourceLegendColor}
            selectedId={selectedCve && selectedCve.id}
            onHover={(cve) => setGlobeTipCve(cve || null)}
            onSelect={(cve) => {
              setGlobeTipCve(cve || null);
              setSelectedCve(selectedCve && selectedCve.id === cve.id ? null : cve);
            }}
          />
        </GlobeWrap>
        {selectedCve && (
          <CveModal>
            <div style={{ color: '#ffaa00', fontSize: 11, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>Описание уязвимости</div>
            <div style={{ color: '#ffcc00', fontWeight: 700, marginBottom: 6 }}>
              {selectedCve.id} {selectedCve.cvss != null && `· оценка CVSS: ${selectedCve.cvss}`}
              {' · источники: ' + ((selectedCve.sources && selectedCve.sources.length) ? selectedCve.sources.join(', ') : (selectedCve.source || 'scan'))}
            </div>
            <div style={{ color: '#00ff88', marginBottom: 8 }}>{selectedCve.title || 'Без названия'}</div>
            <div style={{ color: '#ccc', fontSize: 12, lineHeight: 1.6 }}>{selectedCve.description || 'Описание отсутствует'}</div>
            <button onClick={() => setSelectedCve(null)} style={{ marginTop: 12, background: 'transparent', border: '1px solid #ffcc00', color: '#ffcc00', fontFamily: 'inherit', fontSize: 11, padding: '4px 12px', cursor: 'pointer', borderRadius: 4 }}>Закрыть</button>
          </CveModal>
        )}
      </VizSection>
    );
  };
  const renderSiteContent = () => {
    const raw = reportData?.raw_data || {};
    const dump = (obj) => (
      <pre style={{ color: '#00ff88', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {obj == null ? 'Нет данных' : (typeof obj === 'string' ? obj : JSON.stringify(obj, null, 2))}
      </pre>
    );

    switch (siteActiveTab) {
      case 'overview': {
        const tip = reportData?.target;
        const ipsMap = raw.subdomain_ips || {};
        const mainIP = (ipsMap[tip] || [])[0] || Object.values(ipsMap).flat().find(Boolean) || null;
        const btn = {
          marginTop: 0,
          background: 'rgba(0,20,12,0.85)',
          border: '1px solid #00ff88',
          color: '#00ff88',
          borderRadius: 8,
          padding: '10px 16px',
          fontFamily: 'inherit',
          fontSize: 13,
          letterSpacing: 1,
          cursor: 'pointer',
        };
        return (
          <div style={{ color: '#00ff88', fontSize: 14, lineHeight: 1.65 }}>
            <div style={{
              border: '1px solid rgba(0,255,136,0.45)', borderRadius: 8,
              padding: '12px 14px', marginBottom: 12,
              background: 'rgba(0,12,8,0.5)'
            }}>
              <b>Цель:</b> {tip || '—'} <span style={{ opacity: 0.5 }}>|</span> <b>Тип:</b> {reportData?.type || '—'}
            </div>

            {typeof renderRiskGauge === 'function' ? renderRiskGauge(false) : null}

            <div style={{
              border: '1px solid rgba(0,255,136,0.45)', borderRadius: 8,
              padding: '12px 14px', marginBottom: 14,
              background: 'rgba(0,12,8,0.5)',
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10
            }}>
              {sslResults && !sslResults.error && (
              <div style={{
                border: '1px solid rgba(0,200,255,0.35)', borderRadius: 8, padding: '10px 12px',
                marginBottom: 12, background: 'rgba(0,20,30,0.45)', fontSize: 13, color: '#88ddff'
              }}>
                <b>🔒 TLS:</b> {(sslResults.issuer || '').slice(-60)} · SAN: {(sslResults.san || []).length} ·
                {' '}осталось <b>{sslResults.days_left ?? '?'}</b> дн.
              </div>
            )}
              <span>🌐 <b>Основной IP:</b> {mainIP || '—'}</span>
              {mainIP && (
                <>
                  <button type="button" style={btn} onClick={() => fetchShodan(mainIP)}>SHODAN</button>
                  <button type="button" style={btn} onClick={() => runGeoIP(mainIP)}>GEO</button>
                </>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
              {reportData && (
              <button type="button" style={{ ...btn, color: '#ffcc00', borderColor: '#ffcc00' }}
                onClick={runFullScan} disabled={fullScanning}>
                {fullScanning ? '⚡ FULL SCAN...' : '⚡ FULL SCAN'}
              </button>
              )}
              {reportData && (
              <button type="button" style={{ ...btn, opacity: fullScanDone ? 1 : 0.55 }}
                onClick={downloadPdfReport} disabled={!fullScanDone}>
                📄 Отчёт RU (PDF) {fullScanDone ? '' : '— нужен FULL SCAN'}
              </button>
              )}

            </div>

            {renderFullScanTimeline()}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button type="button" style={btn} onClick={() => { try { if (typeof runAsn === 'function') runAsn(); else setSiteActiveTab('asn'); } catch(e) { setSiteActiveTab('asn'); } }}>🖧 ASN LOOKUP</button>
              <button type="button" style={btn} onClick={() => { try { if (typeof runRelated === 'function') runRelated(); else setSiteActiveTab('related'); } catch(e) { setSiteActiveTab('related'); } }}>🔗 RELATED</button>
              <button type="button" style={btn} onClick={() => setSiteActiveTab('graph')}>📊 Граф связей</button>
            </div>
          </div>
        );
      }

            case 'shodan': return dump(shodanData);
      case 'technologies': return dump(whatwebResults || raw.technologies);
      case 'dirs': return dump(dirResults);
      case 'dns': return dump(dnsResults);
      case 'ssl': return dump(sslResults);
      case 'js': return dump(jsResults);
      case 'geo': return dump(geoResults);
      case 'vhost': return dump(vhostResults);
      case 'robots': return dump(robotsResults);
      case 'admin': return dump(adminResults);
      case 'cloud': return dump(cloudResults);
      case 'security': return dump(secHeadersResults);
      case 'emails': return dump(emailResults);
      case 'git': return dump(gitResults);
      case 'whatweb': return dump(whatwebResults);
      case 'ct': return dump(ctResults);
      case 'nuclei': return dump(nucleiResults);
      case 'wayback': return dump(waybackResults);
      case 'dnssec': return dump(dnssecResults);
      case 'asn_neighbors': return dump(asnNeighborsResults);
      case 'cve': return dump(cveResults);
      case 'cors': return dump(corsResults);
      case 'emaildns': return dump(emailDnsResults);
      case 'asn': return dump(asnResults);
      case 'related': return dump(relatedResults);
      case 'stealth': return dump(stealthScanResults);
      case 'ptr': return dump(ptrResults);
      case 'pdns': return dump(passiveDnsResults);
      case 'mx': return dump(mxResults);
      case 'ipv6': return dump(ipv6Results);
      case 'graphql': return dump(graphqlResults);
      case 'sitemapparams': return dump(sitemapParamsResults);
      case 'versions': return dump(versionCommentsResults);
      case 'mobileapi': return dump(mobileApiResults);
      case 'websocket': return dump(websocketResults);
      case 'tenant': return dump(tenantResults);
      case 'buckets': return dump(cloudBucketsResults);
      case 'thirdparty': return dump(thirdPartyResults);
      case 'typosquat': return dump(typosquatResults);
      case 'graph': return <div style={{ color: '#00ccaa', padding: 12 }}>Граф перенесён в меню слева: <b>📊 ГРАФ СВЯЗЕЙ</b></div>;
      default:
        return <div style={{ color: '#00ff88', opacity: 0.6 }}>Нет данных для вкладки: {siteActiveTab}</div>;
    }
  };


if (!authenticated) {
    return (
      <>
        <GlobalStyle />
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
          backgroundImage: "url('/login-bg.gif')",
          backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
          opacity: loginBgVisible ? 1 : 0,
          transition: 'opacity 1.15s ease-in-out',
        }} />
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
          background: 'rgba(0,0,0,0.35)',
          opacity: loginBgVisible ? 1 : 0,
          transition: 'opacity 1.15s ease-in-out',
        }} />
        <Container style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', position: 'relative',
          zIndex: 2,
          opacity: loginUiReady ? 1 : 0,
          transform: loginUiReady ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.9s ease, transform 0.9s ease',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <LogoText style={{
              fontSize: 42, letterSpacing: 6,
              textShadow: '0 0 28px rgba(0,255,136,0.5), 0 0 60px rgba(0,255,136,0.18)'
            }}>ДАРК-РАЗВЕДКА</LogoText>
            <SubText style={{ letterSpacing: 8, marginTop: 8, opacity: 0.75 }}>RECON ENGINE</SubText>
            <button type="button" onClick={toggleLang} style={{
              marginTop: 14, background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(0,255,136,0.5)',
              color: '#00ff88', fontFamily: 'inherit', fontSize: 11, padding: '5px 16px',
              borderRadius: 20, cursor: 'pointer', letterSpacing: 1
            }}>{lang === 'ru' ? 'EN' : 'RU'}</button>
          </div>
          <div style={{
            width: 380, maxWidth: '92vw',
            background: 'rgba(0, 10, 8, 0.78)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid rgba(0,255,136,0.4)',
            borderRadius: 16,
            padding: '32px 28px 28px',
            boxShadow: '0 0 0 1px rgba(0,255,136,0.06), 0 24px 64px rgba(0,0,0,0.55), 0 0 80px rgba(0,255,136,0.07)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 2,
              background: 'linear-gradient(90deg, transparent, #00ff88, transparent)',
            }} />
            <div style={{
              textAlign: 'center', color: '#00ff88', fontSize: 13,
              letterSpacing: 4, fontWeight: 700, marginBottom: 6
            }}>
              {authStep === 'login' ? 'ACCESS CONTROL' : 'TWO-FACTOR'}
            </div>
            <div style={{
              textAlign: 'center', color: 'rgba(0,255,136,0.4)', fontSize: 10,
              letterSpacing: 3, marginBottom: 22
            }}>
              ENCRYPTED SESSION
            </div>
            <form onSubmit={authStep === 'login' ? handleLogin : handle2FA}>
              {authStep === 'login' ? (
                <>
                  <input name="username" placeholder="Username" type="text" required autoFocus
                    style={{
                      width: '100%', boxSizing: 'border-box', marginBottom: 12, padding: '13px 16px',
                      background: 'rgba(0,0,0,0.5)', color: '#00ff88',
                      border: '1px solid rgba(0,255,136,0.3)', borderRadius: 10,
                      fontFamily: 'inherit', fontSize: 14, outline: 'none'
                    }} />
                  <input name="password" placeholder="Password" type="password" required
                    style={{
                      width: '100%', boxSizing: 'border-box', marginBottom: 18, padding: '13px 16px',
                      background: 'rgba(0,0,0,0.5)', color: '#00ff88',
                      border: '1px solid rgba(0,255,136,0.3)', borderRadius: 10,
                      fontFamily: 'inherit', fontSize: 14, outline: 'none'
                    }} />
                </>
              ) : (
                <input name="totp" placeholder="6-digit code" type="text" required autoFocus
                  style={{
                    width: '100%', boxSizing: 'border-box', marginBottom: 18, padding: '13px 16px',
                    background: 'rgba(0,0,0,0.5)', color: '#00ff88',
                    border: '1px solid rgba(0,255,136,0.3)', borderRadius: 10,
                    fontFamily: 'inherit', fontSize: 14, outline: 'none', letterSpacing: 6, textAlign: 'center'
                  }} />
              )}
              <button type="submit" style={{
                width: '100%', padding: '13px',
                background: 'linear-gradient(135deg, rgba(0,255,136,0.18), rgba(0,180,100,0.08))',
                border: '1px solid #00ff88', color: '#00ff88', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, letterSpacing: 3, fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 0 28px rgba(0,255,136,0.12)'
              }}>
                {authStep === 'login' ? 'AUTHENTICATE →' : 'VERIFY →'}
              </button>
              {loginError && (
                <p style={{ color: '#ff5555', marginTop: 14, textAlign: 'center', fontSize: 13 }}>{loginError}</p>
              )}
            </form>
          </div>
        </Container>
      </>
    );
  }


  return (
    <>
      <GlobalStyle />
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        backgroundImage: "url('/main-bg.gif')",
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
        opacity: bgVisible ? 1 : 0,
        transition: 'opacity 1.2s ease-in-out',
      }} />
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'rgba(5,8,6,0.55)',
        opacity: bgVisible ? 1 : 0,
        transition: 'opacity 1.2s ease-in-out',
      }} />
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
        padding: '8px 16px', pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(5,8,6,0.92), rgba(5,8,6,0.55), transparent)',
      }}>
        <div style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            color: userRole === 'admin' ? '#00ff88' : '#88ccaa',
            border: '1px solid ' + (userRole === 'admin' ? '#00ff88' : 'rgba(0,255,136,0.35)'),
            background: 'rgba(0,12,10,0.85)',
            padding: '6px 12px', borderRadius: 8, fontSize: 12,
            letterSpacing: 1, fontFamily: 'inherit'
          }}>
            {currentUser || '—'} · <b>{userRole === 'admin' ? 'ADMIN' : 'ANALYST'}</b>
          </div>
          <button type="button" onClick={handleLogout} style={{
            fontSize: 11, padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
            color: '#ff8888', border: '1px solid rgba(255,100,100,0.45)',
            background: 'rgba(20,0,0,0.85)', fontFamily: 'inherit', letterSpacing: 1
          }}>Выйти</button>
        </div>
      </div>
      <Container style={{
        position: 'relative', zIndex: 2,
        paddingTop: 52,
        opacity: uiReady ? 1 : 0,
        transform: uiReady ? 'translateY(0)' : 'translateY(14px)',
        transition: 'opacity 0.95s ease, transform 0.95s ease',
      }}>
        {renderDashboardHeader()}
        <Logo>
          <LogoText>ДАРК-РАЗВЕДКА</LogoText>
          <SubText>recon engine</SubText>
          <button onClick={toggleLang} style={{ marginTop: 8, background: 'transparent', border: '1px solid #00ff88', color: '#00ff88', fontFamily: 'inherit', fontSize: 11, padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>
            {lang === 'ru' ? 'EN' : 'RU'}
          </button>
        </Logo>
        <div id="app-shell" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', width: '100%', gap: '12px' }}>
        <MainTabBar style={{ width: sidebarCollapsed ? 56 : 220, minWidth: sidebarCollapsed ? 56 : 200 }}>
          <button type="button" onClick={() => setSidebarCollapsed(v => !v)} title={sidebarCollapsed ? 'Развернуть' : 'Свернуть'}
            style={{ width: '100%', padding: '8px 6px', marginBottom: 6, cursor: 'pointer',
              background: 'rgba(0,20,15,0.6)', color: '#00ff88', border: '1px solid rgba(0,255,136,0.35)',
              borderRadius: 8, fontFamily: 'inherit', fontSize: 14 }}>
            {sidebarCollapsed ? '»' : '« меню'}
          </button>
          <MainTabBtn active={mainTab === 'guide'} onClick={() => setMainTab('guide')}>
            {sidebarCollapsed ? '📖' : '📖 РУКОВОДСТВО ПО ИСПОЛЬЗОВАНИЮ'}
          </MainTabBtn>
          <MainTabBtn active={mainTab === 'scan'} onClick={() => setMainTab('scan')}>
            {sidebarCollapsed ? '🔍' : '🔍 СКАНИРОВАНИЕ'}
          </MainTabBtn>
          <MainTabBtn active={mainTab === 'graph'} onClick={() => setMainTab('graph')}>
            {sidebarCollapsed ? '📊' : '📊 ГРАФ СВЯЗЕЙ'}
          </MainTabBtn>
          <MainTabBtn active={mainTab === 'workplace'} onClick={() => setMainTab('workplace')}>
            {sidebarCollapsed ? '📍' : '📍 МЕСТОПОЛОЖЕНИЕ'}
          </MainTabBtn>
          <MainTabBtn active={mainTab === 'leaks'} onClick={() => setMainTab('leaks')}>
            {sidebarCollapsed ? '🔓' : '🔓 УТЕЧКИ ПО ПАРОЛЯМ'}
          </MainTabBtn>
          <MainTabBtn active={mainTab === 'history'} onClick={() => { setMainTab('history'); loadHistory(); }}>
            {sidebarCollapsed ? '📜' : '📜 ИСТОРИЯ СКАНОВ'}
          </MainTabBtn>
          <MainTabBtn active={mainTab === 'compare'} onClick={() => setMainTab('compare')}>
            {sidebarCollapsed ? '🔀' : '🔀 СРАВНЕНИЕ'}
          </MainTabBtn>
          {userRole === 'admin' && (
            <MainTabBtn active={mainTab === 'audit'} onClick={() => { setMainTab('audit'); loadAudit(); }}>
              {sidebarCollapsed ? '📋' : '📋 АУДИТ'}
            </MainTabBtn>
          )}
        </MainTabBar>
        <div id="app-content" style={{ flex: 1, minWidth: 0, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
        {mainTab === 'guide' && renderGuide()}
        {mainTab === 'workplace' && renderWorkplace()}
        {mainTab === 'leaks' && renderLeaks()}
{mainTab === 'history' && renderHistory()}
        {mainTab === 'compare' && renderCompare()}
        {mainTab === 'audit' && userRole === 'admin' && renderAudit()}
        {mainTab === 'graph' && renderGraphMainPage()}
        {mainTab === 'scan' && (
        <LayoutRow>
        <ChatCol>
        <MainPanel>
          <PanelTitle>🌐 Разведка по сайтам</PanelTitle>
          <PanelContent>
            
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
              marginBottom: 12, padding: '10px 12px', borderRadius: 10,
              border: '1px solid rgba(0,255,136,0.2)', background: 'rgba(0,12,10,0.5)'
            }}>
              <span style={{ color: '#00ccaa', fontSize: 12, fontWeight: 700, marginRight: 8 }}>МОДУЛЬ</span>
              <select
                value={siteActiveTab}
                onChange={(e) => setSiteActiveTab(e.target.value)}
                style={{
                  flex: '1 1 200px', minWidth: 180, maxWidth: 420,
                  background: 'rgba(0,10,8,0.9)', color: '#00ff88', border: '1px solid rgba(0,255,136,0.4)',
                  borderRadius: 8, padding: '10px 12px', fontFamily: 'inherit', fontSize: 14
                }}
              >
                <optgroup label="Основное">
                  <option value="overview">📋 Обзор</option>
                  <option value="subdomains">🔗 Поддомены</option>
                  <option value="whois">🌐 WHOIS</option>
                  <option value="technologies">📡 Технологии</option>
                  <option value="shodan">🛡 Shodan</option>
                  <option value="cve">🛡 CVE</option>
                </optgroup>
                <optgroup label="Web / DNS">
                  <option value="dns">📋 DNS</option>
                  <option value="ssl">🔒 SSL</option>
                  <option value="security">🔒 Заголовки</option>
                  <option value="dirs">📂 Директории</option>
                  <option value="admin">👁 Админки</option>
                  <option value="robots">🤖 robots</option>
                  <option value="js">📜 JS</option>
                  <option value="git">🗄 Git</option>
                  <option value="cors">🔓 CORS</option>
                </optgroup>
                <optgroup label="Разведка+">
                  <option value="ct">📜 CT</option>
                  <option value="nuclei">☢ Nuclei</option>
                  <option value="wayback">📚 Wayback</option>
                  <option value="dnssec">🔐 DNSSEC</option>
                  <option value="whatweb">🧩 WhatWeb</option>
                  <option value="emaildns">📬 SPF/DKIM</option>
                  <option value="emails">📧 Email</option>
                  <option value="related">🔗 Related</option>
                  <option value="asn">🏛 ASN</option>
                  <option value="asn_neighbors">🌐 ASN соседи</option>
                </optgroup>
                <optgroup label="Прочее">
                  <option value="geo">📍 Geo</option>
                  <option value="vhost">🏨 VHost</option>
                  <option value="cloud">☁ Облака</option>
                  <option value="pdns">📡 Passive DNS</option>
                  <option value="mx">✉️ MX</option>
                  <option value="ipv6">🧬 IPv6</option>
                  <option value="graphql">🕸 GraphQL</option>
                  <option value="ptr">🧭 PTR</option>
                  <option value="buckets">🪣 Buckets</option>
                  <option value="typosquat">🎭 Typosquat</option>
                </optgroup>
              </select>
              {[
                ['overview', '📋 Обзор'],
                ['subdomains', '🔗 Subs'],
                ['shodan', '🛡 Shodan'],
                ['cve', 'CVE'],
                ['nuclei', '☢'],
              ].map(([k, lab]) => (
                <button key={k} type="button" onClick={() => setSiteActiveTab(k)} style={{
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
                  border: siteActiveTab === k ? '1px solid #00ff88' : '1px solid rgba(0,255,136,0.25)',
                  background: siteActiveTab === k ? 'rgba(0,255,136,0.2)' : 'transparent',
                  color: '#00ff88'
                }}>{lab}</button>
              ))}
            </div>

                        {baseReconBusy && (
              <div style={{
                marginBottom: 12, padding: '12px 14px', borderRadius: 10,
                border: '1px solid rgba(255,204,0,0.55)', background: 'rgba(40,30,0,0.45)',
                color: '#ffdd66', fontSize: 14, lineHeight: 1.5
              }}>
                <span style={{ display: 'inline-block', animation: 'pulse 1.2s infinite' }}>⏳</span>
                {' '}{t('Базовая разведка… passive DNS, поддомены, технологии (до 3 мин)', 'Base recon in progress…')}
              </div>
            )}

{!reportData && !baseReconBusy && renderScanEmptyHero()}
            <LogPanel>
              {siteLog.slice(-100).map((line, i) => (
                <div key={`log-${siteLog.length}-${i}`} style={{ ...logLineStyle(line), animation: 'logFadeIn 0.35s ease' }}>{line}</div>
              ))}
            </LogPanel>
            {renderSiteContent()}
          </PanelContent>
          <div>
            <Prompt>
              <span style={{color:'#00ff88',marginRight:8, fontWeight: 700}}>➜</span>
              <Input type="text" value={siteCommand} onChange={(e) => setSiteCommand(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); siteExecute(e); } }}
                placeholder="Введите домен или IP-адрес..." autoFocus />
              <SendButton type="button" disabled={baseReconBusy} onClick={(e) => { e.preventDefault(); siteExecute(e); }}><span>⚡</span> {baseReconBusy ? '…' : 'ОТПРАВИТЬ'}</SendButton>
            </Prompt>
          </div>
        </MainPanel>
        </ChatCol>
        <GlobeCol>
          {renderCveGlobe() || (
            <VizSection>
              <div style={{ color: '#00ccaa', fontSize: 18, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>
                🌐 Карта уязвимостей CVE
              </div>
              <EmptyHero style={{ marginTop: 24, borderStyle: 'solid' }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>🌐</div>
                <p style={{ opacity: 0.8, lineHeight: 1.6, margin: 0 }}>
                  CVE появятся после базовой разведки и <b>FULL SCAN</b><br/>
                  Nuclei · Shodan · WhatWeb · NVD/CIRCL
                </p>
              </EmptyHero>
            </VizSection>
          )}
        </GlobeCol>
        </LayoutRow>
        )}
        </div>
        </div>
      </Container>
    </>
  )
}

export default App;
